import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { StatusBadge } from '../components/StatusBadge';
import { DeliveryRules } from '../components/DeliveryRules';
import { DeliveryTabs } from '../components/DeliveryTabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import VerifyDropsPanel from '../components/VerifyDropsPanel';
import { getAvailableDeliveries, getMyDeliveries, getMyOffers, createOffer, askPreBidQuestion, updateDeliveryStatus, createConnectOnboarding, getConnectStatus, getConnectDashboard, getDriverEarnings, driverOnTheWay, getCounties, verifyDelivery, uploadProfilePhoto, getDriverProfile, updateDriverBio, getMyReviews, editOffer, withdrawOffer, resolveAssetUrl, getMessagesUnreadCount, updateDriverUseMode, getMessageStarters, saveMessageStarters } from '../services/api';
import DownloadStatementDialog from '../components/DownloadStatementDialog';
import ServiceAreasPanel from '../components/ServiceAreasPanel';
import PaymentPreferencesPanel from '../components/PaymentPreferencesPanel';
import InsurancePanel from '../components/InsurancePanel';
import VehicleDetailsPanel from '../components/VehicleDetailsPanel';
import CollapsibleDirectoryEnquiries from '../components/CollapsibleDirectoryEnquiries';
import MyPublicProfileView from '../components/MyPublicProfileView';
import DriverUseModeCard from '../components/DriverUseModeCard';
import BrandingPanel from '../components/BrandingPanel';
import InsuranceDeclarationModal from '../components/InsuranceDeclarationModal';
import ProfilePhotoPolicyNotice from '../components/ProfilePhotoPolicyNotice';
import ProfileStrengthCard from '../components/driver/ProfileStrengthCard';
import { MAX_ACTIVE_BIDS, isBiddingClosedFor } from '../config/bidding';
import useAutoRefresh from '../hooks/useAutoRefresh';
import LastUpdatedIndicator from '../components/LastUpdatedIndicator';
import RouteMap from '../components/RouteMap';
import RecentCompletedDeliveries from '../components/RecentCompletedDeliveries';
import JobsMapView from '../components/JobsMapView';
import DeliveryTimeBadge from '../components/DeliveryTimeBadge';
import LiftingHelpSummary from '../components/LiftingHelpSummary';
import { ContactCard } from '../components/ContactCard';
import { containsPhoneNumber, PHONE_BLOCK_MSG_BID } from '../lib/textSafety';
import DriverNav from '../components/DriverNav';
import MessagesInbox from '../components/MessagesInbox';
import { PremiumBadge } from '../components/PremiumBadge';
import SupportChat from '../components/SupportChat';
import { NotificationPrompt, NotificationToggle } from '../components/NotificationPrompt';
import { SongPlayer } from '../components/SongPlayer';
import { formatDuration } from '../lib/formatDuration';
import { feeBreakdown, driverAccessFeeFor, MAX_BID_AMOUNT, MAX_BID_ERROR } from '../lib/fees';
import CancelBookingButton from '../components/CancelBookingButton';
import DriverWhatsAppGroup from '../components/DriverWhatsAppGroup';
import { DIRECTORY_ENABLED } from '../config/features';
import DriverPaymentBreakdown from '../components/DriverPaymentBreakdown';
import DashboardFooter from '../components/DashboardFooter';
import AskAQuestion from '../components/AskAQuestion';
import { toast } from 'sonner';
import { Package, MapPin, Clock, DollarSign, FileText, User, Check, Euro, Wallet, ExternalLink, AlertCircle, CheckCircle, Banknote, TrendingUp, BarChart3, Target, CalendarDays, Navigation, Ruler, Weight, Camera, Filter, HelpCircle, ChevronUp, ChevronDown, KeyRound, Upload, X, Star, MessageSquare, Pencil, Truck, Download, Info, Users, Tag, Zap, CreditCard, Copy, Globe, Shield, PartyPopper, Bell, Flame, ArrowRight, Coins } from 'lucide-react';

// Relative time helper — turns an ISO timestamp into "5m ago" / "2h ago" / "3d ago".
function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  const d = Math.floor(sec / 86400);
  if (d < 30) return `${d}d ago`;
  const m = Math.floor(d / 30);
  return `${m}mo ago`;
}

// Pre-booking location privacy: drivers see the county + approximate town
// only. The backend strips the exact address, Eircode and precise
// coordinates until the customer books and pays, and flags the job with
// `location_precise: false`.
const isAreaOnly = (job) => job?.location_precise === false;
const areaLabel = (area, county) =>
  [county, area].filter(Boolean).join(' – ') || 'Area not provided';
const AREA_ONLY_NOTE = 'Approximate area — the exact address and Eircode are shared once the customer books you.';


export default function DriverDashboard() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // useLocation gives us the URL hash too; useSearchParams only tracks the
  // query string, so we rely on `location.hash` for #reviews-style scrolls.
  const location = useLocation();
  // DoneDeal-style top/bottom navigation — one of:
  // 'home' | 'jobs' | 'messages' | 'my-work' | 'earnings' | 'profile'
  const [activeView, setActiveView] = useState(() => searchParams.get('view') || 'home');
  // Keep `activeView` in sync whenever the URL query string changes from the
  // OUTSIDE (e.g. the Navbar's "Profile & settings" / "My reviews" dropdown
  // calls navigate('/driver?view=profile'). Without this effect the
  // useState initialiser only runs at mount, so clicking those menu items
  // while already on /driver silently does nothing.
  useEffect(() => {
    const v = searchParams.get('view') || 'home';
    setActiveView(prev => (prev === v ? prev : v));
  }, [searchParams]);

  // Enforce use-mode: if a directory-only driver deep-links to /driver?view=jobs
  // (or similar bidding-only view), bounce them back to Home. Same for
  // bidding-only drivers landing on ?view=directory.
  useEffect(() => {
    if (!user || user.role !== 'driver') return;
    const mode = user.driver_use_mode || 'both';
    if (mode === 'both') return;
    const biddingOnlyViews = ['jobs', 'my-work', 'earnings'];
    const directoryOnlyViews = ['directory'];
    if (mode === 'directory' && biddingOnlyViews.includes(activeView)) {
      setActiveView('home');
      setSearchParams({});
    } else if (mode === 'bidding' && directoryOnlyViews.includes(activeView)) {
      setActiveView('home');
      setSearchParams({});
    }
  }, [user, activeView, setSearchParams]);

  // After landing on the profile view via "My reviews" (hash = #reviews),
  // scroll the reviews section into view. Runs whenever the activeView
  // becomes 'profile' AND the URL hash is #reviews. Depends on `location`
  // (not just `activeView`) so re-navigating from /driver?view=profile to
  // /driver?view=profile#reviews still fires the scroll.
  useEffect(() => {
    if (activeView !== 'profile') return;
    if (location.hash !== '#reviews') return;
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById('reviews');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempt < 8) {
        // The reviews section renders after fetchAllData resolves; give it
        // a few short re-tries instead of one immediate attempt.
        setTimeout(() => tryScroll(attempt + 1), 150);
      }
    };
    tryScroll();
  }, [activeView, location]);
  // Messages unread badge (polled every 30 s for near-real-time freshness).
  const [messagesUnread, setMessagesUnread] = useState(0);
  const [enquiriesUnread, setEnquiriesUnread] = useState(0);
  // Public Profile tab: 'view' (default) or 'edit'.
  const [directoryMode, setDirectoryMode] = useState('view');
  // Inner toggle on the "My Work" view: 'jobs' (My Jobs) or 'offers' (My Offers)
  const [workTab, setWorkTab] = useState('jobs');
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [offerData, setOfferData] = useState({ price: '', tags: [], question: '' });
  const [submitting, setSubmitting] = useState(false);
  const [askingQuestion, setAskingQuestion] = useState(false);
  // Driver's own saved opening messages (max 3) — one tap to reuse.
  const [myStarters, setMyStarters] = useState([]);

  useEffect(() => {
    getMessageStarters()
      .then(({ data }) => setMyStarters(data.starters || []))
      .catch(() => setMyStarters([]));
  }, []);

  const persistStarters = async (next) => {
    const previous = myStarters;
    setMyStarters(next);
    try {
      const { data } = await saveMessageStarters(next);
      setMyStarters(data.starters || []);
    } catch (err) {
      setMyStarters(previous);
      toast.error(err?.response?.data?.detail || 'Could not save your message');
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // On The Way state
  const [etaDialogOpen, setEtaDialogOpen] = useState(false);
  const [etaJobId, setEtaJobId] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // County filter state
  const [counties, setCounties] = useState([]);
  const [filterPickupCounty, setFilterPickupCounty] = useState('');
  const [filterDropoffCounty, setFilterDropoffCounty] = useState('');
  const [filterItemType, setFilterItemType] = useState('');
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsSort, setJobsSort] = useState('newest'); // newest | oldest | priority
  const [galleryPhotos, setGalleryPhotos] = useState(null); // array of URLs or null when closed
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [myJobsPage, setMyJobsPage] = useState(1);
  const [myOffersPage, setMyOffersPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsSort, setReviewsSort] = useState('newest'); // newest | oldest | highest | lowest
  const [showInstructions, setShowInstructions] = useState(false);
  // Available jobs view mode: 'list' | 'map'
  const [availableView, setAvailableView] = useState('list');

  // Delivery verification state
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyJobId, setVerifyJobId] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyPhoto, setVerifyPhoto] = useState(null);
  const [verifyPhotoPreview, setVerifyPhotoPreview] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // Profile photo state
  const [profileImage, setProfileImage] = useState(null);
  const [profileStats, setProfileStats] = useState({ avg_rating: 0, review_count: 0 });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [premiumCtaDismissed, setPremiumCtaDismissed] = useState(() => {
    try { return sessionStorage.getItem('jd_premium_cta_dismissed') === '1'; } catch { return false; }
  });
  const dismissPremiumCta = () => {
    try { sessionStorage.setItem('jd_premium_cta_dismissed', '1'); } catch {}
    setPremiumCtaDismissed(true);
  };

  // Bio state
  const [bio, setBio] = useState('');
  const [editingBio, setEditingBio] = useState(false);
  const [bioInput, setBioInput] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  // My reviews state
  const [myReviews, setMyReviews] = useState([]);

  // Insurance state — surfaces in the InsurancePanel (Profile view) AND
  // drives the full-screen InsuranceRequiredModal when not verified.
  const [insurance, setInsurance] = useState(null);

  // Earnings period filter
  const [earningsPeriod, setEarningsPeriod] = useState('this_month');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterPickupCounty) params.pickup_county = filterPickupCounty;
      if (filterDropoffCounty) params.dropoff_county = filterDropoffCounty;
      
      const [available, assigned, offers, connect, earningsData, profile, reviewsData] = await Promise.all([
        getAvailableDeliveries(params),
        getMyDeliveries(),
        getMyOffers(),
        getConnectStatus(),
        getDriverEarnings({ period: earningsPeriod }),
        getDriverProfile(),
        getMyReviews()
      ]);
      setAvailableJobs(available.data);
      setMyJobs(assigned.data);
      setMyOffers(offers.data);
      setConnectStatus(connect.data);
      setEarnings(earningsData.data);
      setProfileImage(profile.data.profile_image);
      setBio(profile.data.bio || '');
      setDriverName(profile.data.name || '');
      setIsPremium(!!profile.data.is_premium);
      setProfileStats({ avg_rating: profile.data.avg_rating, review_count: profile.data.review_count });
      setInsurance(profile.data.insurance || null);
      setMyReviews(reviewsData.data.reviews || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [filterPickupCounty, filterDropoffCounty, earningsPeriod]);

  // Check for Connect return
  useEffect(() => {
    const connectResult = searchParams.get('connect');
    if (connectResult === 'success') {
      toast.success('Stripe Connect setup completed! Checking status...');
      fetchData();
      setSearchParams({});
    } else if (connectResult === 'refresh') {
      toast.info('Please complete your Stripe Connect setup');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 60-second background refresh across the driver surface — available jobs,
  // my jobs (status changes), offers, earnings, connect status. Auto-pauses
  // while tab is hidden.
  const { lastRefreshedAt: autoLastRefreshedAt, isRefreshing: autoIsRefreshing } = useAutoRefresh(fetchData, 60_000);

  // ============ Edit / Withdraw Offer ============
  const [editOfferDialogOpen, setEditOfferDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [editOfferForm, setEditOfferForm] = useState({ price: '', message: '' });
  const [editOfferSubmitting, setEditOfferSubmitting] = useState(false);
  const [withdrawingOfferId, setWithdrawingOfferId] = useState(null);

  const startEditOffer = (offer) => {
    setEditingOffer(offer);
    setEditOfferForm({ price: String(offer.price || ''), message: offer.message || '' });
    setEditOfferDialogOpen(true);
  };

  const handleSaveOfferEdit = async () => {
    if (!editingOffer) return;
    const priceNum = parseFloat(editOfferForm.price);
    if (!priceNum || priceNum < 1) {
      toast.error('Price must be at least €1');
      return;
    }
    if (priceNum > MAX_BID_AMOUNT) {
      toast.error(MAX_BID_ERROR);
      return;
    }
    if (containsPhoneNumber(editOfferForm.message)) {
      toast.error(PHONE_BLOCK_MSG_BID);
      return;
    }
    setEditOfferSubmitting(true);
    try {
      await editOffer(editingOffer.id, { price: priceNum, message: editOfferForm.message });
      toast.success('Bid updated');
      setEditOfferDialogOpen(false);
      setEditingOffer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bid');
    } finally {
      setEditOfferSubmitting(false);
    }
  };

  const handleWithdrawOffer = async (offer) => {
    if (!offer) return;
    if (!window.confirm('Withdraw this bid? The customer will no longer see it.')) return;
    setWithdrawingOfferId(offer.id);
    try {
      await withdrawOffer(offer.id);
      toast.success('Bid withdrawn');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw bid');
    } finally {
      setWithdrawingOfferId(null);
    }
  };

  useEffect(() => {
    getCounties().then(res => setCounties(res.data)).catch(() => {});
  }, []);

  // Poll the Messages unread count every 30 s so the nav badge stays fresh
  // even when the driver is browsing other tabs. The inbox component does
  // its own refresh on mount + every 20 s; this hook is the always-on
  // safety net for "got a customer reply while looking at jobs".
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await getMessagesUnreadCount();
        if (alive) setMessagesUnread(data?.unread_conversations || 0);
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Poll directory-enquiries unread count for the nav badge
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await import('../services/api').then(m => m.listDirectoryEnquiries({ limit: 1 }));
        if (alive) setEnquiriesUnread(data?.unread || 0);
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [activeView]);

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    const priceNum = parseFloat(offerData.price);
    if (!Number.isFinite(priceNum) || priceNum < 1) {
      toast.error('Please enter a valid bid amount (€1 or more).');
      return;
    }
    if (priceNum > MAX_BID_AMOUNT) {
      toast.error(MAX_BID_ERROR);
      return;
    }

    // Compose the bid message: optional question first, then selected tags. The
    // backend caps this at 150 chars and rejects contact info / negotiation
    // phrases — the UI also enforces this client-side below.
    const question = (offerData.question || '').trim();
    const tagsPart = offerData.tags.length > 0 ? offerData.tags.join(', ') : '';
    const message = [question, tagsPart].filter(Boolean).join(question && tagsPart ? ' — ' : '');
    if (message.length > 150) {
      toast.error('Your question + tags exceed 150 characters. Please shorten.');
      return;
    }

    setSubmitting(true);
    try {
      await createOffer(selectedJob.id, {
        price: priceNum,
        message,
      });
      toast.success('Offer submitted successfully!');
      setOfferData({ price: '', tags: [], question: '' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!selectedJob) return;
    const question = (offerData.question || '').trim();
    if (question.length < 5) {
      toast.error('Write your message first (at least 5 characters).');
      return;
    }
    setAskingQuestion(true);
    try {
      await askPreBidQuestion(selectedJob.id, question);
      toast.success('Message sent — the customer will reply in your Messages tab.');
      setOfferData({ price: '', tags: [], question: '' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send your question');
    } finally {
      setAskingQuestion(false);
    }
  };

  const handleMarkComplete = async (jobId) => {
    // Open verify dialog instead of directly completing
    setVerifyJobId(jobId);
    setVerifyCode('');
    setVerifyPhoto(null);
    setVerifyPhotoPreview(null);
    setVerifyError('');
    setVerifyDialogOpen(true);
  };

  const handleVerifyPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVerifyPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setVerifyPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVerifyDelivery = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setVerifyError('Please enter the 6-digit verification code from the customer');
      return;
    }
    if (!verifyPhoto) {
      setVerifyError('Please upload a proof of delivery photo');
      return;
    }
    
    setVerifying(true);
    setVerifyError('');
    try {
      const formData = new FormData();
      formData.append('delivery_code', verifyCode);
      formData.append('photo', verifyPhoto);
      
      await verifyDelivery(verifyJobId, formData);
      toast.success('Delivery verified and completed!');
      setVerifyDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed';
      setVerifyError(detail);
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const handleOnTheWay = async () => {
    if (!estimatedTime || !etaJobId) return;
    setUpdatingStatus(true);
    try {
      await driverOnTheWay(etaJobId, { estimated_arrival: estimatedTime });
      toast.success('Status updated — customer can now see your ETA!');
      setEtaDialogOpen(false);
      setEstimatedTime('');
      setEtaJobId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleUpdateEta = async (jobId) => {
    if (!estimatedTime) return;
    setUpdatingStatus(true);
    try {
      await driverOnTheWay(jobId, { estimated_arrival: estimatedTime });
      toast.success('ETA updated!');
      setEtaDialogOpen(false);
      setEstimatedTime('');
      setEtaJobId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ETA');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await createConnectOnboarding(originUrl);
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Connect setup');
      setConnectLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await getConnectDashboard();
      window.open(response.data.dashboard_url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open dashboard');
    }
  };

  const formatDate = (dateString) => {
    try {
      const parsed = JSON.parse(dateString);
      const d = new Date(parsed.date + 'T00:00:00');
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const fmtTime = (t) => { const [h,m] = t.split(':'); const hr = parseInt(h); return `${hr > 12 ? hr-12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };
      if (parsed.type === 'before') return `${dateStr} by ${fmtTime(parsed.before)}`;
      if (parsed.type === 'range') return `${dateStr}, ${fmtTime(parsed.from)} – ${fmtTime(parsed.to)}`;
      return `${dateStr}, Flexible`;
    } catch {
      const d = new Date(dateString);
      const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${date} by ${time}`;
    }
  };

  const getDeliverySubtext = (dateString) => {
    try {
      const parsed = JSON.parse(dateString);
      if (parsed.type === 'before') return 'Must be delivered before this time';
      if (parsed.type === 'range') return 'Deliver within this time window';
      return 'Flexible — deliver any time this day';
    } catch {
      return 'Must be delivered by this time';
    }
  };

  const hasOfferedOnJob = (jobId) => {
    return myOffers.some(offer => offer.job_id === jobId);
  };

  const getMyOfferForJob = (jobId) => {
    return myOffers.find(offer => offer.job_id === jobId);
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await uploadProfilePhoto(formData);
      setProfileImage(res.data.profile_image);
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveBio = async () => {
    if (bioInput.length > 50) { toast.error('Bio must be 50 characters or less'); return; }
    setSavingBio(true);
    try {
      await updateDriverBio(bioInput);
      setBio(bioInput);
      setEditingBio(false);
      toast.success('Bio updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bio');
    } finally {
      setSavingBio(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0" data-testid="driver-dashboard">
      <Navbar />

      {/* Insurance Declaration blocker — shown until the driver ticks the
          Driver Insurance Declaration (Feb-2026 pivot). Legacy accounts
          who haven't yet accepted see this modal on any dashboard load.
          Existing jobs and messages remain reachable via the top nav (which
          sits above the overlay). New bids are blocked server-side too. */}
      {insurance && insurance.declared === false && (
        <InsuranceDeclarationModal
          onDeclared={(res) => setInsurance({ ...(insurance || {}), declared: true, declared_at: res?.declared_at })}
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* DoneDeal-style navigation: top pill bar on desktop, bottom tab bar on mobile */}
        <DriverNav
          active={activeView}
          onChange={(k) => { setActiveView(k); setSearchParams(k === 'home' ? {} : { view: k }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          useMode={user?.driver_use_mode || 'both'}
          counts={{
            jobs: availableJobs.length,
            work: myJobs.filter(j => ['assigned','on_the_way'].includes(j.status)).length + myOffers.filter(o => (o.display_status_key || o.status || 'pending') === 'pending').length,
            messages: messagesUnread,
            enquiries: enquiriesUnread,
          }}
        />

        {/* Messages tab — central inbox for all conversations (offer threads). */}
        {activeView === 'messages' && (
          <div className="mb-6" data-testid="driver-messages-view">
            <MessagesInbox role="driver" onUnreadChange={setMessagesUnread} />
          </div>
        )}

        {/* Mode switcher — only when the public directory feature is live.
            With it off, every driver is bidding-only (migrated at startup). */}
        {activeView === 'home' && DIRECTORY_ENABLED && user?.role === 'driver' && (
          <div className="mb-6" data-testid="home-use-mode-block">
            <DriverUseModeCard
              currentMode={user.driver_use_mode || 'both'}
              onChanged={checkAuth}
              compact
            />
          </div>
        )}

        {activeView === 'home' && <NotificationPrompt role="driver" />}

        {/* Animated delivery van — only on Home for a calmer dashboard */}
        {activeView === 'home' && (
        <div className="overflow-hidden h-8 relative mb-6" data-testid="driver-animated-van">
          <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200"></div>
          <div className="animated-van absolute" style={{ top: 0 }}>
            <Truck className="w-7 h-7 text-[#2E7D32]" strokeWidth={1.5} />
          </div>
        </div>
        )}

        {activeView === 'home' && <SongPlayer testId="driver-song-player" />}

        {/* ============ BIDDING PROFILE VIEW ============ */}
        {activeView === 'profile' && (
        <>
        <div className="mb-5" data-testid="bidding-profile-clarifier">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Bidding Profile</h1>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed max-w-2xl">
            Everything here is used when you bid on JustDeliver jobs — your insurance declaration, how you take payment,
            your vehicles, the counties you cover and your profile photo.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start" data-testid="bidding-profile-container">

          {/* ── LEFT RAIL: identity, strength, extras ── */}
          <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-6">

            {/* Identity card */}
            <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden" data-testid="driver-identity-card">
              <div aria-hidden="true" className="h-20 bg-gradient-to-br from-[#1B5E20] via-[#2E7D32] to-emerald-500" />
              <div className="px-5 pb-5 -mt-12 text-center">
                {/* Profile photo — overlaps the banner */}
                <div className="relative inline-block" data-testid="driver-profile-photo">
                  <label className="cursor-pointer block group" title="Upload a profile photo">
                    {profileImage ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${profileImage}`}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
                        onError={(e) => { e.target.onerror=null; e.target.style.display='none'; e.target.parentElement.querySelector('.photo-fallback').style.display='flex'; }}
                      />
                    ) : null}
                    <div
                      className="photo-fallback w-24 h-24 rounded-full bg-slate-100 items-center justify-center border-4 border-white shadow-md ring-2 ring-dashed ring-slate-200 group-hover:ring-[#2E7D32] transition-all"
                      style={{ display: profileImage ? 'none' : 'flex' }}
                    >
                      <Camera className="w-7 h-7 text-slate-300" />
                    </div>
                    <span className="absolute bottom-0.5 right-0.5 w-7 h-7 rounded-full bg-[#1B5E20] text-white border-2 border-white shadow flex items-center justify-center group-hover:bg-[#144718] transition-colors">
                      <Camera className="w-3.5 h-3.5" />
                    </span>
                    <input type="file" accept="image/*" onChange={handleProfilePhotoUpload} className="hidden" data-testid="profile-photo-input" />
                    {uploadingPhoto && <div className="absolute inset-0 bg-white/70 rounded-full flex items-center justify-center"><span className="text-xs font-medium text-slate-500">Uploading…</span></div>}
                  </label>
                </div>

                <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-slate-900 truncate" data-testid="driver-greeting">
                    {driverName || 'Your profile'}
                  </h2>
                  {isPremium && <PremiumBadge size="sm" dataTestId="driver-premium-badge" />}
                </div>

                {profileStats.review_count > 0 ? (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-full px-3 py-1" data-testid="driver-rating-badge">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-bold text-amber-800">{profileStats.avg_rating}</span>
                    <span className="text-xs text-amber-600">({profileStats.review_count} reviews)</span>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">No reviews yet</p>
                )}

                {/* Bio */}
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid="driver-bio-section">
                  {editingBio ? (
                    <div className="space-y-2">
                      <Input
                        value={bioInput}
                        onChange={(e) => setBioInput(e.target.value.replace(/\d/g, '').slice(0, 50))}
                        placeholder="Short bio (text only, no numbers)…"
                        className="h-9 text-sm rounded-lg"
                        maxLength={50}
                        data-testid="bio-input"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-400">{bioInput.length}/50</span>
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="ghost" onClick={() => setEditingBio(false)} className="h-8 text-xs px-2">Cancel</Button>
                          <Button size="sm" onClick={handleSaveBio} disabled={savingBio} className="h-8 bg-[#1B5E20] text-white hover:bg-[#144718] text-xs px-3 rounded-lg" data-testid="bio-save-btn">
                            {savingBio ? '…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setBioInput(bio); setEditingBio(true); }}
                      className="w-full text-left flex items-start gap-2 rounded-lg px-2 py-1.5 -mx-2 hover:bg-slate-50 transition-colors group"
                      data-testid="bio-edit-btn"
                    >
                      {bio ? (
                        <p className="flex-1 text-sm text-slate-600 italic" data-testid="bio-text">&ldquo;{bio}&rdquo;</p>
                      ) : (
                        <p className="flex-1 text-sm text-slate-400" data-testid="bio-empty">Add a short bio so customers know who you are</p>
                      )}
                      <Pencil className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#2E7D32] flex-shrink-0 mt-0.5" />
                    </button>
                  )}
                </div>

                {!profileImage && (
                  <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5 text-left" data-testid="photo-recommendation">
                    <Camera className="w-3.5 h-3.5 flex-shrink-0" /> Add a profile photo to get more jobs
                  </p>
                )}
              </div>

              {/* Photo rules — collapsed, right where the photo is uploaded */}
              <div className="px-5 pb-5">
                <ProfilePhotoPolicyNotice />
              </div>
            </div>

            {/* Profile strength */}
            <ProfileStrengthCard
              hasPhoto={!!profileImage}
              hasBio={!!bio}
              insuranceDeclared={!!insurance?.declared}
            />

            {/* Push notifications */}
            <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm p-5 flex items-start gap-3" data-testid="driver-notification-settings">
              <span className="p-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 flex-shrink-0">
                <Bell className="w-4 h-4" strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">Push notifications</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">Instant alerts for new jobs in your area, accepted bids and delivery updates.</p>
                <div className="mt-3">
                  <NotificationToggle />
                </div>
              </div>
            </div>

            {/* Premium teaser — compact, dismissible */}
            {!premiumCtaDismissed && (
              <div className="relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5" data-testid="premium-cta-card">
                <button
                  type="button"
                  onClick={dismissPremiumCta}
                  aria-label="Hide premium driver promo"
                  data-testid="premium-cta-close"
                  className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white/80 hover:bg-white text-amber-800 border border-amber-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
                <div className="flex items-center gap-2.5 pr-6">
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-sm flex-shrink-0">
                    <Star className="w-4 h-4 text-white fill-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-amber-900 leading-tight">Become a Premium Driver</p>
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-900 text-amber-100 text-[9px] font-bold uppercase tracking-wider" data-testid="premium-cta-coming-soon">
                      Coming soon
                    </span>
                  </div>
                </div>
                <p className="text-xs text-amber-900/80 leading-snug mt-3">
                  Win more bids — get a <span className="font-semibold">⭐ Premium Driver 🚚</span> badge that customers see on every offer.
                </p>
                <div className="mt-2.5 space-y-1 text-xs text-amber-900/90 font-medium">
                  <p className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Higher win rate</p>
                  <p className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Trust badge on bids</p>
                  <p className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Priority in search</p>
                </div>
                <Button
                  disabled
                  className="mt-4 w-full bg-gradient-to-br from-yellow-500 to-orange-500 text-white font-bold rounded-lg gap-2 h-10 cursor-not-allowed opacity-95"
                  data-testid="premium-cta-btn"
                >
                  <Star className="w-4 h-4 fill-white" /> Join Premium
                </Button>
              </div>
            )}
          </aside>

          {/* ── MAIN RAIL: what customers see on your bids ── */}
          <div className="lg:col-span-8 space-y-5 [&>*]:!mb-0">
        <InsurancePanel onChange={(rec) => setInsurance(rec)} />

        <PaymentPreferencesPanel />

        <div className="mb-6">
          <VehicleDetailsPanel />
        </div>

        <ServiceAreasPanel />

        <div className="mb-8" id="reviews" data-testid="driver-reviews-section">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-yellow-500" strokeWidth={1.5} />
              <h2 className="text-lg font-semibold font-['Inter'] text-gray-900">My Reviews</h2>
              {profileStats.review_count > 0 && (
                <span className="inline-flex items-center gap-1 text-sm bg-yellow-50 px-2 py-0.5 border border-yellow-200">
                  <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{profileStats.avg_rating}</span>
                  <span className="text-gray-400 text-xs">({profileStats.review_count} review{profileStats.review_count !== 1 ? 's' : ''})</span>
                </span>
              )}
            </div>
            {myReviews.length > 1 && (
              <div className="flex items-center gap-2">
                <label htmlFor="reviews-sort" className="text-xs text-gray-500">Sort by</label>
                <select
                  id="reviews-sort"
                  value={reviewsSort}
                  onChange={(e) => { setReviewsSort(e.target.value); setReviewsPage(1); }}
                  className="border border-gray-200 rounded-md px-2 py-1 text-xs bg-white"
                  data-testid="reviews-sort-select"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="highest">Highest rated</option>
                  <option value="lowest">Lowest rated</option>
                </select>
              </div>
            )}
          </div>
          {myReviews.length === 0 ? (
            <Card className="border border-gray-200 shadow-none">
              <CardContent className="py-8 text-center">
                <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No reviews yet. Complete deliveries to start getting reviews!</p>
              </CardContent>
            </Card>
          ) : (
            (() => {
              const REVIEWS_PER_PAGE = 10;
              const sortedReviews = [...myReviews].sort((a, b) => {
                if (reviewsSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
                if (reviewsSort === 'highest') return (b.rating - a.rating) || (new Date(b.created_at) - new Date(a.created_at));
                if (reviewsSort === 'lowest') return (a.rating - b.rating) || (new Date(b.created_at) - new Date(a.created_at));
                return new Date(b.created_at) - new Date(a.created_at); // newest
              });
              const totalReviewPages = Math.ceil(sortedReviews.length / REVIEWS_PER_PAGE);
              const currentReviewPage = Math.min(reviewsPage, totalReviewPages || 1);
              const pagedReviews = sortedReviews.slice(
                (currentReviewPage - 1) * REVIEWS_PER_PAGE,
                currentReviewPage * REVIEWS_PER_PAGE
              );
              return (
                <>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-reviews-grid">
                    {pagedReviews.map((review, i) => {
                      const absIdx = (currentReviewPage - 1) * REVIEWS_PER_PAGE + i;
                      return (
                        <Card key={absIdx} className="border border-gray-200 shadow-none" data-testid={`my-review-${absIdx}`}>
                          <CardContent className="pt-4 pb-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex gap-0.5">
                                {[1,2,3,4,5].map(s => (
                                  <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                                ))}
                              </div>
                              <span className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <p className="text-xs text-gray-500 font-medium">{review.customer_name}</p>
                            {review.comment && <p className="text-sm text-gray-700 mt-1 italic">"{review.comment}"</p>}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  {totalReviewPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4" data-testid="reviews-pagination">
                      <button
                        onClick={() => setReviewsPage(p => Math.max(1, p - 1))}
                        disabled={currentReviewPage === 1}
                        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="reviews-prev-btn"
                      >Prev</button>
                      {Array.from({ length: totalReviewPages }, (_, i) => i + 1).map(p => (
                        <button
                          key={p}
                          onClick={() => setReviewsPage(p)}
                          className={`w-8 h-8 text-sm font-medium rounded-md ${p === currentReviewPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}
                          data-testid={`reviews-page-${p}`}
                        >{p}</button>
                      ))}
                      <button
                        onClick={() => setReviewsPage(p => Math.min(totalReviewPages, p + 1))}
                        disabled={currentReviewPage === totalReviewPages}
                        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="reviews-next-btn"
                      >Next</button>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
          </div>
        </div>
        </>
        )}

        {/* ============ HOME VIEW: Stripe Connect Status + Delivery Rules + Quick Stats ============ */}
        {activeView === 'home' && (<>
        {/* Welcome / hero */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="driver-home-greeting">
            Welcome back{driverName ? `, ${driverName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{
              !DIRECTORY_ENABLED
                ? 'Find jobs, manage deliveries, and track earnings.'
                : ((user?.driver_use_mode || 'both') === 'directory'
                  ? 'Manage your public driver profile and enquiries.'
                  : (user?.driver_use_mode === 'bidding'
                    ? 'Find jobs, manage deliveries, and track earnings.'
                    : 'Find jobs, manage deliveries, and grow your public profile.'))
            }</span>
            <LastUpdatedIndicator lastRefreshedAt={autoLastRefreshedAt} isRefreshing={autoIsRefreshing} />
          </p>
        </div>

        {/* Quick stat row — hidden for directory-only drivers (they don't bid or earn on JD) */}
        {(user?.driver_use_mode || 'both') !== 'directory' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="driver-home-quickstats">
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Available jobs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{availableJobs.length}</p>
              <button onClick={() => setActiveView('jobs')} className="text-xs font-semibold text-[#2E7D32] hover:underline mt-1" data-testid="home-cta-jobs">Browse →</button>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Active deliveries</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{myJobs.filter(j => ['assigned','on_the_way'].includes(j.status)).length}</p>
              <button onClick={() => { setActiveView('my-work'); setWorkTab('jobs'); }} className="text-xs font-semibold text-[#2E7D32] hover:underline mt-1" data-testid="home-cta-work">View →</button>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Pending offers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{myOffers.filter(o => (o.display_status_key || o.status || 'pending') === 'pending').length}</p>
              <button onClick={() => { setActiveView('my-work'); setWorkTab('offers'); }} className="text-xs font-semibold text-[#2E7D32] hover:underline mt-1" data-testid="home-cta-offers">View →</button>
            </CardContent>
          </Card>
          <Card className="border border-gray-200 shadow-none">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Earned this month</p>
              <p className="text-2xl font-bold text-[#2E7D32] mt-1">€{earnings?.period_earned?.toFixed(2) || earnings?.monthly_earned?.toFixed(2) || '0.00'}</p>
              <button onClick={() => setActiveView('earnings')} className="text-xs font-semibold text-[#2E7D32] hover:underline mt-1" data-testid="home-cta-earnings">Details →</button>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Driver announcement WhatsApp group */}
        <DriverWhatsAppGroup />

        {/* Pay Driver Directly — no payout account, no Stripe setup needed to bid or get paid */}
        {(user?.driver_use_mode || 'both') !== 'directory' && (
          <div className="mb-6 border-2 border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="direct-payment-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-200 flex items-center justify-center rounded-full flex-shrink-0">
                <Banknote className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900 text-sm">Customers pay you directly</p>
                <p className="text-xs text-emerald-700">No payout account needed — choose how you want to be paid and bid straight away.</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveView('profile')}
              className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="direct-payment-methods-btn"
            >
              <Wallet className="w-4 h-4" />
              Payment methods
            </Button>
          </div>
        )}

        {/* Bidding-only drivers: upsell them to add a public profile too */}
        {DIRECTORY_ENABLED && user?.role === 'driver' && (user?.driver_use_mode === 'bidding') && (
          <div className="mb-6 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 rounded-lg" data-testid="add-public-profile-upsell">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-emerald-900 text-base">Add a public driver profile</h3>
                <p className="text-sm text-emerald-800/80 mt-1 leading-snug">Let customers find you on <span className="font-semibold">JustDeliver.ie/drivers</span> and contact you directly — no extra fees, no bidding required.</p>
                <Button
                  onClick={async () => { try { await updateDriverUseMode('both'); await checkAuth(); toast.success('Public profile enabled. Head to the Public Profile tab to set it up.'); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to enable public profile'); } }}
                  className="mt-3 bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-sm h-10 px-5 text-sm font-bold gap-2"
                  data-testid="enable-public-profile-btn"
                >
                  <Globe className="w-4 h-4" /> Enable public profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* How It Works */}
        <Card className="mb-6 border border-gray-200 shadow-none overflow-hidden" data-testid="driver-instructions">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
            data-testid="toggle-driver-instructions-btn"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <HelpCircle className="w-4 h-4 text-[#2E7D32]" /> How It Works — Driver Guide
            </span>
            {showInstructions ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showInstructions && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#2E7D32] text-white text-xs flex items-center justify-center rounded-full font-bold">1</span>
                    Choose How You Get Paid
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1.5 ml-8">
                    <li>No payout account or bank setup needed — customers pay you <strong>directly</strong></li>
                    <li>Open <strong>Bidding Profile</strong> and tick the methods you accept</li>
                    <li>Choose from <strong>Cash, Revolut, Bank Transfer, ZipPay</strong> or <strong>Other</strong></li>
                    <li>Your methods are shown to customers before they book you</li>
                    <li>You can start bidding straight away</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#2E7D32] text-white text-xs flex items-center justify-center rounded-full font-bold">2</span>
                    Submit Offers on Jobs
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1.5 ml-8">
                    <li>Browse <strong>"Available Jobs"</strong> in the tab below</li>
                    <li>Use the county filters to find jobs in your area</li>
                    <li>Check item photos, dimensions, and weight before bidding</li>
                    <li>Click <strong>"Make Offer"</strong> and enter your price</li>
                    <li>Add a message to stand out (e.g. "Can collect today")</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#2E7D32] text-white text-xs flex items-center justify-center rounded-full font-bold">3</span>
                    Fee Structure
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1.5 ml-8">
                    <li>The customer pays JustDeliver online: a flat <strong>€10 Booking Deposit</strong> plus the <strong>JustDeliver Driver Fee</strong></li>
                    <li>The €10 Booking Deposit is <strong>never</strong> taken out of your money</li>
                    <li>JustDeliver Driver Fee: <strong>€0</strong> on bids €1–€99 · <strong>10% of your bid</strong> on bids €100–€1,200</li>
                    <li>You collect the rest <strong>directly from the customer</strong> — you never send us anything</li>
                    <li>Example: Bid €95 → you collect <strong>€95</strong> directly (no deduction under €100)</li>
                    <li>Example: Bid €200 → deduction €20, you collect <strong>€180</strong> directly</li>
                    <li>We always show your exact amount before you bid — you never have to work out the 10% yourself</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#2E7D32] text-white text-xs flex items-center justify-center rounded-full font-bold">4</span>
                    On The Way & Completing Jobs
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1.5 ml-8">
                    <li>When a customer accepts your offer, it appears in <strong>"My Jobs"</strong></li>
                    <li>Click <strong>"On The Way"</strong> and set your ETA — the customer will be notified</li>
                    <li>You can update your ETA at any time</li>
                    <li>Once delivered, click <strong>"Confirm Delivery"</strong> and enter the customer's 6-digit code</li>
                    <li>Upload a proof photo — this protects you and builds your reputation</li>
                    <li>Collect the balance directly from the customer using your agreed payment method</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Card>

        <div className="mb-6">
          <DeliveryRules />
        </div>
        </>)}

        {/* ============ AVAILABLE JOBS VIEW ============ */}
        {activeView === 'jobs' && (
          <div className="mb-5">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight" data-testid="jobs-view-title">Available Jobs</h1>
            <p className="text-sm sm:text-base text-gray-500 mt-1.5">{availableJobs.length} {availableJobs.length === 1 ? 'job' : 'jobs'} matching your area</p>
          </div>
        )}
        {activeView === 'jobs' && (
          <>
            {/* Available Jobs */}
            <div data-testid="available-jobs-content">
            {/* Filters — redesigned: Pickup area as the prominent primary
             * filter (green-bordered card), Drop-off + Item as compact side-
             * by-side pills, sort + view-toggle on their own rows. */}
            <div className="mb-5 space-y-3" data-testid="county-filter-section">

              {/* Row 1 — funnel icon + prominent Pickup area card */}
              <div className="flex items-stretch gap-2.5">
                <div className="flex items-center justify-center w-9 flex-shrink-0">
                  <Filter className="w-5 h-5 text-gray-400" strokeWidth={1.75} />
                </div>
                <label
                  htmlFor="filter-pickup-county-native"
                  className="relative flex-1 flex items-center gap-3 bg-white border-2 border-[#2E7D32] rounded-2xl px-3.5 sm:px-4 py-3 cursor-pointer hover:bg-emerald-50/30 transition-colors"
                  data-testid="filter-pickup-county-card"
                >
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#2E7D32]" strokeWidth={2} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-[#2E7D32] leading-none">Pickup area</span>
                    <span className="block text-base sm:text-lg font-extrabold text-gray-900 mt-1 leading-tight truncate">
                      {filterPickupCounty || 'All Ireland'}
                    </span>
                  </span>
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <select
                    id="filter-pickup-county-native"
                    value={filterPickupCounty}
                    onChange={(e) => { setFilterPickupCounty(e.target.value); setJobsPage(1); }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
                    data-testid="filter-pickup-county"
                  >
                    <option value="">All Ireland</option>
                    {counties.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>

              {/* Row 2 — Drop-off + Item side-by-side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <FilterPill
                  Icon={MapPin}
                  prefix="Drop-off:"
                  value={filterDropoffCounty || 'All'}
                  testId="filter-dropoff-county-card"
                >
                  <select
                    value={filterDropoffCounty}
                    onChange={(e) => { setFilterDropoffCounty(e.target.value); setJobsPage(1); }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
                    data-testid="filter-dropoff-county"
                  >
                    <option value="">All</option>
                    {counties.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FilterPill>
                <FilterPill
                  Icon={Package}
                  prefix="Item:"
                  value={(() => {
                    const map = {appliance:'Appliance', sofa:'Sofa / Couch', furniture:'Furniture', electronics:'Electronics', pallet:'Pallet', package:'Package / Box', envelope:'Envelope', house_move:'House Move', other:'Other'};
                    return map[filterItemType] || 'All';
                  })()}
                  testId="filter-item-type-card"
                >
                  <select
                    value={filterItemType}
                    onChange={(e) => { setFilterItemType(e.target.value); setJobsPage(1); }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
                    data-testid="filter-item-type"
                  >
                    <option value="">All</option>
                    <option value="appliance">Appliance</option>
                    <option value="sofa">Sofa / Couch</option>
                    <option value="furniture">Furniture</option>
                    <option value="electronics">Electronics</option>
                    <option value="pallet">Pallet</option>
                    <option value="house_move">House Move</option>
                    <option value="package">Package / Box</option>
                    <option value="envelope">Envelope</option>
                    <option value="other">Other</option>
                  </select>
                </FilterPill>
              </div>

              {/* Row 3 — full-width sort pill */}
              <FilterPill
                Icon={CalendarDays}
                value={(() => {
                  if (jobsSort === 'oldest') return 'Oldest first';
                  if (jobsSort === 'priority') return 'Priority (urgent + fewer bids)';
                  return 'Newest first';
                })()}
                testId="filter-jobs-sort-card"
              >
                <select
                  value={jobsSort}
                  onChange={(e) => { setJobsSort(e.target.value); setJobsPage(1); }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
                  data-testid="filter-jobs-sort"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="priority">Priority (urgent + fewer bids)</option>
                </select>
              </FilterPill>

              {/* Row 4 — Clear-link (when active) + List/Grid/Map view toggle */}
              <div className="flex items-center justify-between gap-2">
                {(filterPickupCounty || filterDropoffCounty || filterItemType) ? (
                  <button
                    type="button"
                    onClick={() => { setFilterPickupCounty(''); setFilterDropoffCounty(''); setFilterItemType(''); setJobsPage(1); }}
                    className="text-xs text-gray-500 hover:text-gray-800 underline font-medium"
                    data-testid="clear-filters-btn"
                  >
                    Clear filters
                  </button>
                ) : <span />}
                <div className="inline-flex bg-gray-100 rounded-xl p-1" data-testid="available-view-toggle">
                  <button
                    type="button"
                    onClick={() => setAvailableView('list')}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors ${availableView === 'list' ? 'bg-[#2E7D32] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid="available-view-list-btn"
                  >
                    <FileText className="w-4 h-4" /> List
                  </button>
                  <button
                    type="button"
                    onClick={() => setAvailableView('grid')}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors ${availableView === 'grid' ? 'bg-[#2E7D32] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid="available-view-grid-btn"
                  >
                    <BarChart3 className="w-4 h-4 rotate-90" /> Grid
                  </button>
                  <button
                    type="button"
                    onClick={() => setAvailableView('map')}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors ${availableView === 'map' ? 'bg-[#2E7D32] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid="available-view-map-btn"
                  >
                    <MapPin className="w-4 h-4" /> Map
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Package className="w-8 h-8 text-gray-400 animate-pulse" />
              </div>
            ) : availableView === 'map' ? (
              (() => {
                const filtered = filterItemType ? availableJobs.filter(j => j.item_type === filterItemType) : availableJobs;
                return (
                  <JobsMapView
                    isPremium={isPremium}
                    jobs={filtered}
                    onBidJob={(job) => { setSelectedJob(job); setDialogOpen(true); }}
                    height="540px"
                  />
                );
              })()
            ) : (() => {
              let filtered = filterItemType ? availableJobs.filter(j => j.item_type === filterItemType) : [...availableJobs];
              // Apply sort. Priority = urgent time slot first, then fewer active bids,
              // then newer first (so undiscovered jobs surface before crowded ones).
              if (jobsSort === 'oldest') {
                filtered.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
              } else if (jobsSort === 'priority') {
                const urgent = (j) => j.preferred_time === 'urgent' || j.preferred_time === 'asap' ? 0 : 1;
                filtered.sort((a, b) => {
                  const u = urgent(a) - urgent(b);
                  if (u !== 0) return u;
                  const bidsDiff = (a.active_bid_count || 0) - (b.active_bid_count || 0);
                  if (bidsDiff !== 0) return bidsDiff;
                  return (b.created_at || '').localeCompare(a.created_at || '');
                });
              } else {
                // newest first (default)
                filtered.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
              }
              const JOBS_PER_PAGE = 10;
              const totalPages = Math.ceil(filtered.length / JOBS_PER_PAGE);
              const paged = filtered.slice((jobsPage - 1) * JOBS_PER_PAGE, jobsPage * JOBS_PER_PAGE);
              return filtered.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-500">No jobs match your filters</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or check back later</p>
                </div>
              ) : (
                <>
                <div
                  className={availableView === 'grid'
                    ? 'grid gap-3 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                    : 'space-y-2'}
                  data-testid={`available-jobs-${availableView}`}
                >
                  {paged.map((job) => (
                    <div
                      key={job.id}
                      className="border border-gray-200 rounded-xl bg-white hover:shadow-lg hover:-translate-y-px transition-all duration-150 overflow-hidden min-w-0"
                      data-testid={`available-job-${job.id}`}
                    >
                      <div className="flex">
                        {/* Content */}
                        <div className={`flex-1 min-w-0 ${availableView === 'grid' ? 'p-3 sm:p-4' : 'p-4 sm:p-5'}`}>
                          {/* ── 1. Job ID + live bidding status ─────────── */}
                          {(() => {
                            const biddingClosed = isBiddingClosedFor(job.active_bid_count, isPremium);
                            return (
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                  {job.delivery_ref && (
                                    <span
                                      className="font-mono text-[11px] sm:text-xs font-bold text-[#1B5E20] bg-emerald-50 px-2 py-1 rounded-md"
                                      data-testid={`job-ref-${job.id}`}
                                    >
                                      {job.delivery_ref}
                                    </span>
                                  )}
                                  {job.quick_post && (
                                    <span
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-800 text-[10px] font-bold rounded uppercase tracking-wide"
                                      title="Posted via Quick Post — email verified"
                                      data-testid={`quick-post-verified-${job.id}`}
                                    >
                                      <Zap className="w-2.5 h-2.5" fill="currentColor" /> Email verified
                                    </span>
                                  )}
                                  {Array.isArray(job.additional_drops) && job.additional_drops.length > 0 && (
                                    <span
                                      className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded"
                                      title={`One pickup, ${job.additional_drops.length + 1} total drop-offs`}
                                      data-testid={`multi-drop-pill-${job.id}`}
                                    >
                                      Multi-drop • {job.additional_drops.length + 1} stops
                                    </span>
                                  )}
                                  {job.created_at && (
                                    <span className="text-[11px] text-gray-400" data-testid={`job-posted-${job.id}`} title={new Date(job.created_at).toLocaleString()}>
                                      Posted {timeAgo(job.created_at)}
                                    </span>
                                  )}
                                </div>
                                {biddingClosed ? (
                                  <span
                                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-bold"
                                    data-testid={`job-bidding-status-${job.id}`}
                                  >
                                    <CheckCircle className="w-3.5 h-3.5" /> Bidding Closed
                                  </span>
                                ) : (
                                  <span
                                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold shadow-sm"
                                    data-testid={`job-bidding-status-${job.id}`}
                                  >
                                    <Flame className="w-3.5 h-3.5" fill="currentColor" /> Live Bidding
                                  </span>
                                )}
                              </div>
                            );
                          })()}

                          {/* ── 2. Collection → Delivery ─────────────────── */}
                          {(() => {
                            const collection = isAreaOnly(job)
                              ? areaLabel(job.pickup_area, job.pickup_county)
                              : (job.pickup_county || job.pickup_location);
                            const delivery = isAreaOnly(job)
                              ? areaLabel(job.dropoff_area, job.dropoff_county)
                              : (job.dropoff_county || job.dropoff_location);
                            const viaSegments = (job.drops || [])
                              .map((d) => d.county || d.area)
                              .filter(Boolean);
                            const titleAttr = `${job.pickup_location} → ${job.dropoff_location}`;
                            const isGridView = availableView === 'grid';
                            return (
                              <>
                                <div
                                  className={`rounded-xl bg-emerald-50/70 px-3 py-2.5 min-w-0 ${isGridView ? 'flex flex-col gap-2' : 'flex items-center gap-2 sm:gap-3'}`}
                                  title={titleAttr}
                                  data-testid={`job-route-${job.id}`}
                                >
                                  <span className={`flex items-start gap-2 min-w-0 ${isGridView ? 'w-full' : 'flex-1'}`}>
                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-[11px] text-gray-500 leading-none">Collection</span>
                                      <span
                                        className="block text-sm sm:text-base font-extrabold text-gray-900 leading-snug break-words"
                                        data-testid={`job-pickup-area-${job.id}`}
                                      >
                                        {collection}
                                      </span>
                                    </span>
                                  </span>
                                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 text-[#2E7D32] flex-shrink-0 ${isGridView ? 'rotate-90 self-center' : ''}`} strokeWidth={2.5} />
                                  <span className={`flex items-start gap-2 min-w-0 ${isGridView ? 'w-full' : 'flex-1'}`}>
                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-[11px] text-gray-500 leading-none">Delivery</span>
                                      <span
                                        className="block text-sm sm:text-base font-extrabold text-gray-900 leading-snug break-words"
                                        data-testid={`job-dropoff-area-${job.id}`}
                                      >
                                        {delivery}
                                      </span>
                                    </span>
                                  </span>
                                </div>
                                {viaSegments.length > 0 && (
                                  <p className="mt-1.5 text-[11px] text-gray-500">
                                    via {viaSegments.join(' → ')}
                                  </p>
                                )}
                                {isAreaOnly(job) ? (
                                  <p className="mt-1.5 text-[11px] text-gray-500 italic" data-testid={`job-area-note-${job.id}`}>
                                    {AREA_ONLY_NOTE}
                                  </p>
                                ) : (
                                  <p className="mt-1.5 text-[11px] text-gray-500" data-testid={`job-eircodes-${job.id}`}>
                                    Eircodes:{' '}
                                    <span className="font-mono font-semibold text-gray-700">{job.pickup_eircode || '—'}</span>
                                    {' → '}
                                    <span className="font-mono font-semibold text-gray-700">{job.dropoff_eircode || '—'}</span>
                                  </p>
                                )}
                              </>
                            );
                          })()}

                          {/* ── 3. Delivery info: timeframe · distance · time ── */}
                          {(job.preferred_time || job.distance_km != null || job.duration_min != null) && (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-2">
                            <DeliveryTimeBadge preferredTime={job.preferred_time} size="md" dataTestId={`job-time-badge-${job.id}`} />
                            {job.distance_km != null && (
                              <>
                                <span className="h-5 w-px bg-gray-200" aria-hidden />
                                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900" data-testid={`job-distance-${job.id}`}>
                                  <MapPin className="w-4 h-4 text-[#2E7D32]" strokeWidth={2.25} /> {job.distance_km} km
                                </span>
                              </>
                            )}
                            {job.duration_min != null && (
                              <>
                                <span className="h-5 w-px bg-gray-200" aria-hidden />
                                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900" data-testid={`job-duration-${job.id}`}>
                                  <Clock className="w-4 h-4 text-[#2E7D32]" strokeWidth={2.25} /> {formatDuration(job.duration_min)}
                                </span>
                              </>
                            )}
                          </div>
                          )}

                          {/* ── 4. Item: photo · category · description · size ── */}
                          {(job.item_description || job.item_type || job.weight_kg || job.dimensions_length || (job.photos || []).length > 0) && (
                            <div className={`mt-3 pt-3 border-t border-gray-100 flex ${availableView === 'grid' ? 'flex-col gap-2' : 'gap-3'}`} data-testid={`job-item-callout-${job.id}`}>
                              {Array.isArray(job.photos) && job.photos.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => { setGalleryPhotos(job.photos); setGalleryIdx(0); }}
                                  className={`relative rounded-lg overflow-hidden bg-gray-50 hover:ring-2 hover:ring-[#2E7D32]/40 transition-all ${availableView === 'grid' ? 'w-full aspect-[4/3]' : 'w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0'}`}
                                  title="Tap to view photos"
                                  data-testid={`job-photo-${job.id}-0`}
                                >
                                  <img
                                    src={resolveAssetUrl(job.photos[0])}
                                    alt="Item"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  {job.photos.length > 1 && (
                                    <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-bold">
                                      +{job.photos.length - 1}
                                    </span>
                                  )}
                                </button>
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                  <Package className="w-6 h-6 text-[#1B5E20]" strokeWidth={1.5} />
                                </div>
                              )}
                              <div className={availableView === 'grid' ? 'w-full min-w-0' : 'flex-1 min-w-0'}>
                                {job.item_type && (
                                  <p className="flex items-center gap-1.5 text-sm font-semibold text-[#2E7D32] capitalize" data-testid={`job-item-type-pill-${job.id}`}>
                                    <Package className="w-4 h-4" strokeWidth={2} /> {job.item_type}
                                  </p>
                                )}
                                {job.item_description && (
                                  <p className="mt-0.5 text-base sm:text-lg font-extrabold text-gray-900 leading-snug break-words" data-testid={`job-item-desc-${job.id}`}>
                                    {job.item_description}
                                  </p>
                                )}
                                {(job.weight_kg || job.dimensions_length || job.dimensions_width || job.dimensions_height) && (
                                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                                    {job.weight_kg && (
                                      <span className="flex items-center gap-1.5"><Weight className="w-4 h-4 text-gray-400" /> {job.weight_kg} kg</span>
                                    )}
                                    {(job.dimensions_length || job.dimensions_width || job.dimensions_height) && (
                                      <>
                                        {job.weight_kg && <span className="h-4 w-px bg-gray-200" aria-hidden />}
                                        <span className="flex items-center gap-1.5" data-testid={`job-dimensions-${job.id}`}>
                                          <Ruler className="w-4 h-4 text-gray-400" />
                                          {[job.dimensions_length, job.dimensions_width, job.dimensions_height].map(v => v ? `${v}` : '?').join(' × ')} cm
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* ── 5. Customer budget (guide price) ─────────── */}
                          <div
                            className="mt-3 rounded-lg bg-blue-50 px-3 py-2 flex items-center gap-2 flex-wrap"
                            data-testid={`job-customer-budget-${job.id}`}
                          >
                            <Coins className="w-4 h-4 text-blue-700 flex-shrink-0" strokeWidth={2} />
                            <span className="text-sm font-extrabold text-blue-900">
                              Customer Budget: {job.customer_budget != null ? `€${Number(job.customer_budget).toFixed(0)}` : 'Not specified'}
                            </span>
                            <span className="hidden sm:inline h-4 w-px bg-blue-200" aria-hidden />
                            <span className="flex items-start gap-1 text-[11px] text-blue-800/80">
                              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" /> Guide only — you can bid above or below.
                            </span>
                          </div>

                          {/* ── 6. Load / Unload / Driver assistance ─────── */}
                          <LiftingHelpSummary
                            collectionHelp={job.collection_help}
                            deliveryHelp={job.delivery_help}
                            driverAssistance={job.driver_assistance}
                            columns
                            stack={availableView === 'grid'}
                            dataTestId={`job-lifting-help-${job.id}`}
                          />

                          {/* ── 7. Live bidding panel (live data) ───────── */}
                          {(() => {
                            const biddingClosed = isBiddingClosedFor(job.active_bid_count, isPremium);
                            const count = Math.min(job.active_bid_count || 0, MAX_ACTIVE_BIDS);
                            const remainingLabel = (() => {
                              if (!job.expires_at) return null;
                              const ms = new Date(job.expires_at).getTime() - Date.now();
                              if (ms <= 0) return 'soon';
                              const totalMin = Math.floor(ms / 60000);
                              const h = Math.floor(totalMin / 60);
                              const m = totalMin % 60;
                              return h >= 1 ? `${h}h ${m}m` : `${m}m`;
                            })();
                            return (
                              <div
                                className={`mt-3 rounded-xl px-3 py-2.5 ${biddingClosed ? 'bg-gray-50' : 'bg-amber-50/70'}`}
                                data-testid={biddingClosed ? `job-bidding-closed-${job.id}` : `job-bidding-panel-${job.id}`}
                              >
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="flex items-center gap-2 min-w-0">
                                    {biddingClosed ? (
                                      <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0" strokeWidth={2.25} />
                                    ) : (
                                      <Flame className="w-5 h-5 text-orange-500 flex-shrink-0" fill="currentColor" />
                                    )}
                                    <span
                                      className="text-sm sm:text-base font-extrabold text-gray-900 leading-tight"
                                      data-testid={`job-bidding-headline-${job.id}`}
                                    >
                                      {biddingClosed
                                        ? 'Bidding closed'
                                        : count === 0 ? 'Be the first to bid' : 'Offers being submitted'}
                                    </span>
                                  </span>
                                  <span className="text-xs sm:text-sm whitespace-nowrap">
                                    <span className={`font-bold ${biddingClosed ? 'text-gray-600' : 'text-emerald-700'}`}>
                                      {biddingClosed ? MAX_ACTIVE_BIDS : count} of {MAX_ACTIVE_BIDS}
                                    </span>{' '}
                                    <span className="text-gray-600">offers received</span>
                                  </span>
                                </div>

                                {/* Segmented progress bar */}
                                <div className="mt-2 flex items-center gap-1" aria-hidden>
                                  {Array.from({ length: MAX_ACTIVE_BIDS }).map((_, i) => (
                                    <span
                                      key={i}
                                      className={`h-2 flex-1 min-w-0 rounded-full ${
                                        biddingClosed ? 'bg-gray-300' : i < count ? 'bg-[#2E7D32]' : 'bg-gray-200'
                                      }`}
                                    />
                                  ))}
                                </div>

                                {/* Offer range + time remaining */}
                                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs sm:text-sm">
                                  <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={`job-offer-range-${job.id}`}>
                                    <Tag className="w-4 h-4 text-orange-500 flex-shrink-0" strokeWidth={2} />
                                    <span className="text-gray-600">Offer range:</span>
                                    <span className="font-extrabold text-orange-700 whitespace-nowrap">
                                      {job.bid_range ? `€${job.bid_range.min} – €${job.bid_range.max}` : '—'}
                                    </span>
                                  </span>
                                  <span className="h-4 w-px bg-amber-200" aria-hidden />
                                  <span className="inline-flex items-center gap-1.5" data-testid={`job-driver-expiry-${job.id}`}>
                                    <Clock className="w-4 h-4 text-[#2E7D32] flex-shrink-0" strokeWidth={2} />
                                    <span className="font-extrabold text-gray-900 whitespace-nowrap">{remainingLabel || '—'}</span>
                                    <span className="text-gray-600">remaining</span>
                                  </span>
                                </div>
                              </div>
                            );
                          })()}

                          {/* ── 8. Actions ──────────────────────────────── */}
                          <div className="mt-4 space-y-2">
                            {hasOfferedOnJob(job.id) ? (
                              getMyOfferForJob(job.id)?.status === 'accepted' ? (
                                <div className="w-full py-3 rounded-full bg-green-50 text-center text-sm font-semibold text-green-700" data-testid={`offer-accepted-${job.id}`}>
                                  Accepted €{getMyOfferForJob(job.id)?.price.toFixed(2)}
                                </div>
                              ) : getMyOfferForJob(job.id)?.status === 'rejected' ? (
                                <div className="w-full py-3 rounded-full bg-red-50 text-center text-sm font-medium text-red-600" data-testid={`offer-rejected-${job.id}`}>
                                  Rejected
                                </div>
                              ) : (
                                <div className="w-full py-3 rounded-full bg-gray-50 text-center text-sm font-medium text-gray-600" data-testid={`offer-submitted-${job.id}`}>
                                  Offered €{getMyOfferForJob(job.id)?.price.toFixed(2)}
                                </div>
                              )
                            ) : isBiddingClosedFor(job.active_bid_count, isPremium) ? (
                              <div className="w-full py-3 rounded-full bg-gray-100 text-center text-sm font-medium text-gray-500" data-testid={`bidding-closed-${job.id}`}>
                                Bidding closed
                              </div>
                            ) : (
                              <>
                                <Button
                                  size="lg"
                                  className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-full h-12 text-base font-bold justify-center gap-2"
                                  onClick={() => { setSelectedJob(job); setDialogOpen(true); }}
                                  data-testid={`submit-offer-btn-${job.id}`}
                                >
                                  Submit Your Offer <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                                </Button>
                                <Button
                                  size="lg"
                                  variant="outline"
                                  className="w-full border-[#2E7D32] text-[#1B5E20] hover:bg-emerald-50 rounded-full h-11 text-sm font-bold justify-center gap-2"
                                  onClick={() => { setSelectedJob(job); setDialogOpen(true); }}
                                  data-testid={`ask-question-btn-${job.id}`}
                                >
                                  <MessageSquare className="w-4 h-4" strokeWidth={2.25} /> Ask a Question
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-4" data-testid="jobs-pagination">
                    <button
                      onClick={() => setJobsPage(p => Math.max(1, p - 1))}
                      disabled={jobsPage === 1}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                    >Prev</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => setJobsPage(p)}
                        className={`w-8 h-8 text-sm font-medium rounded-md ${p === jobsPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`page-${p}`}
                      >{p}</button>
                    ))}
                    <button
                      onClick={() => setJobsPage(p => Math.min(totalPages, p + 1))}
                      disabled={jobsPage === totalPages}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                    >Next</button>
                  </div>
                )}
                </>
              );
            })()}
          </div>

          {/* Recent completed deliveries — read-only feed under the live
              jobs list (drivers only). */}
          <RecentCompletedDeliveries />
          </>
        )}

        {/* ── Page-level Bid Dialog (used by both list rows and map pins) ── */}
          {selectedJob && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedJob(null); }}>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="driver-bid-dialog">
                <DialogHeader>
                  <DialogTitle>Submit Your Offer</DialogTitle>
                </DialogHeader>
                {/* Warning banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="offer-warning">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">For your protection, all bookings must stay on JustDeliver. Sharing contact details may result in account suspension.</p>
                </div>

                {/* Pickup & Drop-off Eircodes — dedicated card near the top
                    so drivers can plan the route and calculate distance
                    without hunting through the address block. Tap-to-copy
                    on mobile. */}
                {/* Before booking: collection & delivery AREAS only. The exact
                    address + Eircode unlock for the winning driver once the
                    customer has paid. */}
                {isAreaOnly(selectedJob) ? (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-3" data-testid="bid-dialog-area-card">
                    <div className="flex items-center gap-2 mb-2">
                      <span aria-hidden="true" className="text-base leading-none">📍</span>
                      <h3 className="text-sm font-bold text-emerald-900 tracking-tight">Collection &amp; delivery areas</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        { label: 'Collection', value: areaLabel(selectedJob.pickup_area, selectedJob.pickup_county), testid: 'bid-dialog-pickup-area' },
                        { label: 'Delivery', value: areaLabel(selectedJob.dropoff_area, selectedJob.dropoff_county), testid: 'bid-dialog-dropoff-area' },
                      ].map(({ label, value, testid }) => (
                        <div key={label} className="bg-white rounded-md border border-emerald-200 px-3 py-2 min-h-[52px]" data-testid={testid}>
                          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{label}</div>
                          <div className="text-sm font-bold text-emerald-900 leading-snug">{value}</div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-emerald-800 italic" data-testid="bid-dialog-area-note">
                      {AREA_ONLY_NOTE}
                    </p>
                  </div>
                ) : (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-3" data-testid="bid-dialog-eircode-card">
                  <div className="flex items-center gap-2 mb-2">
                    <span aria-hidden="true" className="text-base leading-none">📍</span>
                    <h3 className="text-sm font-bold text-emerald-900 tracking-tight">Pickup &amp; Drop-off Eircodes</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { label: 'Pickup',  value: selectedJob.pickup_eircode,  testid: 'bid-dialog-pickup-eircode'  },
                      { label: 'Drop-off', value: selectedJob.dropoff_eircode, testid: 'bid-dialog-dropoff-eircode' },
                    ].map(({ label, value, testid }) => (
                      <div key={label} className="flex items-center justify-between gap-2 bg-white rounded-md border border-emerald-200 px-3 py-2 min-h-[52px]" data-testid={testid}>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{label}</div>
                          {value ? (
                            <div className="text-lg font-bold text-emerald-900 font-mono tracking-wide select-all">
                              {value}
                            </div>
                          ) : (
                            <div className="text-sm text-emerald-700/80 italic">Not provided</div>
                          )}
                        </div>
                        {value && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(value);
                                toast.success(`${label} Eircode copied`);
                              } catch {
                                toast.error('Could not copy — long-press to copy manually');
                              }
                            }}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 active:scale-95 transition"
                            data-testid={`${testid}-copy-btn`}
                            aria-label={`Copy ${label} Eircode ${value}`}
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Job details */}
                <div className="space-y-3 pb-3 border-b border-gray-100" data-testid="offer-job-details">
                  {/* Pre-booking, the areas card above already shows these. */}
                  {!isAreaOnly(selectedJob) && (
                  <>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">
                        Pickup
                      </p>
                      <p className="text-sm text-gray-800" data-testid="bid-dialog-pickup-location">{selectedJob.pickup_location}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-900 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">
                        Drop-off
                      </p>
                      <p className="text-sm text-gray-800" data-testid="bid-dialog-dropoff-location">{selectedJob.dropoff_location}</p>
                    </div>
                  </div>
                  </>
                  )}
                  {Array.isArray(selectedJob.additional_drops) && selectedJob.additional_drops.length > 0 && (
                    <div className="flex items-start gap-2" data-testid={`additional-drops-${selectedJob.id}`}>
                      <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-wider text-blue-700 font-medium flex items-center gap-1">
                          Multi-drop • {selectedJob.additional_drops.length} extra {selectedJob.additional_drops.length === 1 ? 'stop' : 'stops'}
                        </p>
                        {/* Per-drop detail cards if structured `drops` array exists,
                            otherwise fall back to the legacy address-only list. */}
                        {Array.isArray(selectedJob.drops) && selectedJob.drops.length > 0 ? (
                          <ol className="mt-2 space-y-2">
                            {selectedJob.drops.map((d, i) => (
                              <li
                                key={i}
                                className="rounded-md border border-blue-100 bg-white p-2.5 text-sm"
                                data-testid={`drop-detail-${i}`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-extrabold flex-shrink-0 mt-0.5">
                                    {i + 2}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 leading-snug break-words">{d.address || '—'}</p>
                                    {d.eircode && (
                                      <p
                                        className="inline-block mt-1 text-[11px] font-bold tracking-wider px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono"
                                        data-testid={`drop-eircode-pill-${i}`}
                                      >
                                        {d.eircode}
                                      </p>
                                    )}
                                    {(d.receiver_name || d.receiver_phone) && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {d.receiver_name || ''}
                                        {d.receiver_name && d.receiver_phone ? ' · ' : ''}
                                        {d.receiver_phone || ''}
                                      </p>
                                    )}
                                    {d.item_description && (
                                      <p className="text-xs text-gray-500 italic mt-0.5 leading-snug break-words">
                                        "{d.item_description}"
                                      </p>
                                    )}
                                    {(d.dimensions_length || d.dimensions_width || d.dimensions_height || d.weight_kg) && (
                                      <p
                                        className="text-[11px] text-gray-600 mt-1 font-mono tabular-nums"
                                        data-testid={`drop-dimensions-display-${i}`}
                                      >
                                        {[
                                          (d.dimensions_length || d.dimensions_width || d.dimensions_height) &&
                                            `${d.dimensions_length ?? '–'}×${d.dimensions_width ?? '–'}×${d.dimensions_height ?? '–'}cm`,
                                          d.weight_kg && `${d.weight_kg}kg`,
                                        ].filter(Boolean).join(' · ')}
                                      </p>
                                    )}
                                    {Array.isArray(d.photos) && d.photos.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`drop-photos-${i}`}>
                                        {d.photos.map((src, pIdx) => (
                                          <a
                                            key={pIdx}
                                            href={src}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50 hover:ring-2 hover:ring-blue-300"
                                          >
                                            <img src={src} alt={`Drop ${i + 2} photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <ul className="text-sm text-gray-800 list-decimal list-inside space-y-0.5 mt-1">
                            {selectedJob.additional_drops.map((addr, i) => (
                              <li key={i} className="leading-snug">{addr}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-[11px] text-blue-700 mt-1.5">Factor these into your bid — one pickup, {selectedJob.additional_drops.length + 1} total stops.</p>
                      </div>
                    </div>
                  )}
                  {/* Route map */}
                  {selectedJob.pickup_lat && selectedJob.pickup_lng && selectedJob.dropoff_lat && selectedJob.dropoff_lng && (
                    <div className="pt-1" data-testid={`driver-route-map-${selectedJob.id}`}>
                      <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium mb-1 flex items-center gap-1">
                        <Navigation className="w-3 h-3" /> Route &amp; distance
                      </p>
                      <RouteMap
                        pickup={{ lat: selectedJob.pickup_lat, lng: selectedJob.pickup_lng, label: selectedJob.pickup_location }}
                        dropoff={{ lat: selectedJob.dropoff_lat, lng: selectedJob.dropoff_lng, label: selectedJob.dropoff_location }}
                        waypoints={(selectedJob.waypoints || []).filter(w => w && w.lat != null && w.lng != null).map((w, i) => ({ lat: w.lat, lng: w.lng, label: w.address || `Drop ${i + 2}` }))}
                        distanceKm={selectedJob.distance_km}
                        durationMin={selectedJob.duration_min}
                        geometry={selectedJob.route_geometry}
                        height="220px"
                      />
                    </div>
                  )}
                  {/* Item — prominent green callout so the driver knows exactly what they're moving before pricing */}
                  {(selectedJob.item_description || selectedJob.item_type || selectedJob.weight_kg || selectedJob.dimensions_length) && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="bid-dialog-item-callout">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Package className="w-4 h-4 text-[#1B5E20]" />
                        <span className="text-[11px] uppercase tracking-wider font-bold text-[#1B5E20]">Item</span>
                        {selectedJob.item_type && (
                          <span className="ml-1 px-1.5 py-0.5 bg-white text-[#1B5E20] border border-emerald-200 text-[10px] font-semibold capitalize rounded">{selectedJob.item_type}</span>
                        )}
                      </div>
                      {selectedJob.item_description && (
                        <p className="text-base font-bold text-[#1B5E20] leading-snug break-words" data-testid="bid-dialog-item-desc">
                          {selectedJob.item_description}
                        </p>
                      )}
                      {(selectedJob.weight_kg || selectedJob.dimensions_length || selectedJob.dimensions_width || selectedJob.dimensions_height) && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm font-semibold text-[#1B5E20]">
                          {selectedJob.weight_kg && <span className="flex items-center gap-1"><Weight className="w-3.5 h-3.5" /> {selectedJob.weight_kg} kg</span>}
                          {(selectedJob.dimensions_length || selectedJob.dimensions_width || selectedJob.dimensions_height) && (
                            <span className="flex items-center gap-1" data-testid="bid-dialog-dimensions">
                              <Ruler className="w-3.5 h-3.5" />
                              {[selectedJob.dimensions_length, selectedJob.dimensions_width, selectedJob.dimensions_height].map(v => v ? `${v}` : '?').join(' × ')} cm
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Customer Budget — guide price (not a maximum). Always
                      shown so drivers know the customer's expectation before
                      they bid. Falls back to "Not specified" per spec. */}
                  <div
                    className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2.5"
                    data-testid="bid-dialog-customer-budget"
                  >
                    <div className="flex items-center gap-1.5">
                      <span aria-hidden="true" className="text-lg leading-none">💶</span>
                      <span className="text-[11px] uppercase tracking-wider font-bold text-indigo-900">Customer Budget</span>
                    </div>
                    {selectedJob.customer_budget != null ? (
                      <p className="text-base font-bold text-indigo-900 leading-snug mt-0.5">
                        €{Number(selectedJob.customer_budget).toFixed(0)} <span className="text-xs font-medium text-indigo-700">(Guide Price)</span>
                      </p>
                    ) : (
                      <p className="text-sm font-medium text-indigo-700 mt-0.5">Not specified</p>
                    )}
                    <p className="text-[11px] text-indigo-700/80 mt-1 leading-snug">
                      Guide only — you can bid above or below.
                    </p>
                  </div>
                </div>
                <form onSubmit={handleSubmitOffer} className="space-y-5 pt-1">
                  {/* Job context — timeframe + bid status in a single quiet header strip */}
                  <div className="rounded-lg border border-gray-200 bg-white p-3.5 space-y-2.5" data-testid="bid-dialog-job-context">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Delivery timeframe</p>
                        <div className="mt-1">
                          <DeliveryTimeBadge preferredTime={selectedJob.preferred_time} size="lg" dataTestId="bid-dialog-time-badge" />
                        </div>
                      </div>
                      {selectedJob && (() => {
                        const biddingClosed = isBiddingClosedFor(selectedJob.active_bid_count, isPremium);
                        const count = Math.min(selectedJob.active_bid_count || 0, MAX_ACTIVE_BIDS);
                        if (biddingClosed) {
                          return (
                            <div className="text-right" data-testid="bid-dialog-bidding-closed">
                              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Status</p>
                              <p className="text-sm font-semibold text-gray-700 mt-1">Bidding closed</p>
                            </div>
                          );
                        }
                        return (
                          <div className="text-right" data-testid="bid-dialog-bid-counter">
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Active bids</p>
                            <p className="text-sm font-semibold text-gray-900 mt-1 tabular-nums">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2E7D32] animate-pulse mr-1.5 align-middle" />
                              {count} <span className="text-gray-400 font-normal">/ {MAX_ACTIVE_BIDS}</span>
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                    {selectedJob.bid_range && (selectedJob.active_bid_count || 0) < MAX_ACTIVE_BIDS && (
                      <p className="text-xs text-gray-500" data-testid="bid-dialog-bid-range">
                        <TrendingUp className="w-3 h-3 inline mr-1 text-gray-400" />
                        Typical offers: <span className="font-semibold text-gray-700">€{selectedJob.bid_range.min}–€{selectedJob.bid_range.max}</span>
                      </p>
                    )}
                    {isPremium && (selectedJob.active_bid_count || 0) >= MAX_ACTIVE_BIDS && (
                      <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5 font-medium" data-testid="premium-bid-override-note">
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 flex-shrink-0 mt-0.5" />
                        <span>Premium perk: this job already has {MAX_ACTIVE_BIDS} bids — you can still place yours.</span>
                      </p>
                    )}
                    <p className="text-[11px] text-gray-500 leading-snug">
                      Customers reject drivers who can&apos;t meet the timeframe — factor it into your bid.
                    </p>
                  </div>

                  {/* Lifting & loading help — kept; minor visual tightening lives inside the component */}
                  <LiftingHelpSummary
                    collectionHelp={selectedJob.collection_help}
                    deliveryHelp={selectedJob.delivery_help}
                    driverAssistance={selectedJob.driver_assistance}
                    dataTestId="bid-dialog-lifting-help"
                  />

                  {/* Your bid — price input + clean breakdown */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="price" className="text-sm font-semibold text-gray-800">Your bid (€)</Label>
                      <span className="text-[11px] text-gray-400">Max €{MAX_BID_AMOUNT.toLocaleString()}</span>
                    </div>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="1"
                        max={MAX_BID_AMOUNT}
                        placeholder="0.00"
                        value={offerData.price}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') return setOfferData(prev => ({ ...prev, price: '' }));
                          const n = parseFloat(raw);
                          const next = Number.isFinite(n) && n > MAX_BID_AMOUNT ? String(MAX_BID_AMOUNT) : raw;
                          setOfferData(prev => ({ ...prev, price: next }));
                        }}
                        className={`pl-10 h-11 rounded-md text-lg font-semibold tabular-nums ${Number(offerData.price) > MAX_BID_AMOUNT ? 'border-red-300 focus-visible:ring-red-300' : 'border-gray-200 focus-visible:ring-[#2E7D32]/30'}`}
                        required
                        data-testid="offer-price-input"
                      />
                    </div>
                    {Number(offerData.price) > MAX_BID_AMOUNT && (
                      <p className="text-xs text-red-600 font-medium" data-testid="bid-max-error">{MAX_BID_ERROR}</p>
                    )}

                    {offerData.price ? (() => {
                      const b = feeBreakdown(offerData.price);
                      return (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3.5 text-sm" data-testid="bid-fee-breakdown">
                          <div className="space-y-1.5 text-gray-600">
                            <div className="flex items-center justify-between">
                              <span>Your bid</span>
                              <span className="tabular-nums text-gray-800 font-medium">€{b.bid.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Booking Deposit (customer pays JustDeliver)</span>
                              <span className="tabular-nums">+ €{b.bookingFee.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-gray-100">
                              <span className="text-gray-700">Customer pays in total</span>
                              <span className="tabular-nums text-gray-900 font-semibold">€{b.customerTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between" data-testid="bid-driver-access-fee-row">
                              <span>{b.accessFee > 0 ? 'JustDeliver Driver Fee (10%) — paid online by customer' : 'JustDeliver Driver Fee (none under €100)'}</span>
                              <span className="tabular-nums">− €{b.accessFee.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-200">
                            <span className="text-sm font-semibold text-gray-900">Customer pays you directly</span>
                            <span
                              className="text-xl font-extrabold text-[#1B5E20] tabular-nums leading-none"
                              data-testid="bid-driver-payout"
                            >
                              €{b.driverPayout.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-2">
                            You collect this amount directly from the customer — no payout account needed. The €10 Booking Deposit is never taken from your money.
                          </p>
                        </div>
                      );
                    })() : (
                      <p className="text-xs text-gray-400 mt-1">Enter your price to see the full breakdown.</p>
                    )}

                    {/* VAT pricing notice — collapsed, neutral */}
                    <details className="mt-1 rounded-md text-xs text-gray-600 group" data-testid="driver-vat-notice">
                      <summary className="cursor-pointer flex items-center gap-1.5 list-none text-[#1B5E20] font-semibold hover:underline">
                        <Info className="w-3.5 h-3.5" /> Pricing &amp; VAT — read before bidding
                        <ChevronDown className="w-3.5 h-3.5 ml-auto text-gray-400 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="mt-2 pl-5 leading-relaxed space-y-1.5">
                        <p>Please enter your <span className="font-semibold text-gray-700">final</span> price.</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          <li>If <span className="font-semibold">VAT registered</span>, include 23% VAT in your bid.</li>
                          <li>If <span className="font-semibold">not VAT registered</span>, simply enter your normal price.</li>
                        </ul>
                        <p className="text-gray-500">Customer sees this as the total — no extra VAT added later.</p>
                        <p className="text-gray-500"><span className="font-semibold text-gray-700">Example:</span> Want €50 + VAT? Enter <span className="font-semibold text-gray-700">€61.50</span>.</p>
                      </div>
                    </details>
                  </div>

                  {/* Anonymity note — per-job temporary username */}
                  <div className="flex items-start gap-2.5 rounded-lg border border-blue-100 bg-blue-50/70 p-3" data-testid="bid-anonymity-note">
                    <Shield className="w-4 h-4 text-[#1565C0] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="text-xs text-[#0D47A1] leading-relaxed">
                      <span className="font-semibold">You bid anonymously.</span> The customer sees a random username
                      (e.g. <span className="font-semibold">Vexor482</span>) instead of your name, and a brand-new one is
                      generated every time you bid on a job. Your real name is only shared once they choose you and pay.
                    </p>
                  </div>

                  {/* Ask a Question — kept */}
                  <div className="space-y-1.5" data-testid="bid-question-section">
                    <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                      <span aria-hidden="true">💬</span> Message or question <span className="text-[11px] font-normal text-gray-400">(optional)</span>
                    </Label>
                    {/* One-tap starters — the driver's own saved openers first,
                        then a few built-in suggestions. Tapping fills the box
                        so it can still be edited. */}
                    <div className="flex flex-wrap gap-1.5" data-testid="bid-message-starters">
                      {myStarters.map((text, i) => (
                        <span
                          key={`mine-${i}`}
                          className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 overflow-hidden"
                          data-testid={`bid-my-starter-${i}`}
                        >
                          <button
                            type="button"
                            onClick={() => setOfferData(prev => ({ ...prev, question: text.slice(0, 150) }))}
                            className="max-w-[180px] truncate px-2.5 py-1 text-[11px] font-semibold text-[#1B5E20] hover:bg-emerald-100 transition-colors"
                            title={text}
                            data-testid={`bid-my-starter-use-${i}`}
                          >
                            ⭐ {text}
                          </button>
                          <button
                            type="button"
                            onClick={() => persistStarters(myStarters.filter((_, idx) => idx !== i))}
                            className="px-1.5 py-1 text-emerald-700/60 hover:text-red-600 hover:bg-emerald-100 transition-colors"
                            title="Delete this saved message"
                            data-testid={`bid-my-starter-delete-${i}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {[
                        { key: 'intro', label: '👋 Friendly intro', text: "I'd be happy to complete this delivery for you — please get back to me as soon as you can. Thank you." },
                        { key: 'load', label: 'Help to load?', text: 'Will somebody be available to help load at collection?' },
                        { key: 'stairs', label: 'Any stairs?', text: 'Are there any stairs or tight access at either end?' },
                      ].map(({ key, label, text }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setOfferData(prev => ({ ...prev, question: text.slice(0, 150) }))}
                          className="px-2.5 py-1 rounded-full border border-gray-200 text-[11px] font-semibold text-gray-700 hover:border-[#2E7D32] hover:text-[#1B5E20] hover:bg-emerald-50 transition-colors"
                          data-testid={`bid-message-starter-${key}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <Textarea
                      value={offerData.question}
                      onChange={(e) => setOfferData(prev => ({ ...prev, question: e.target.value.slice(0, 150) }))}
                      placeholder={'e.g. "I\'d be happy to complete this delivery for you" or "Will somebody help load?"'}
                      maxLength={150}
                      rows={2}
                      className="rounded-md text-sm border-gray-200 focus-visible:ring-[#2E7D32]/30"
                      data-testid="bid-question-input"
                    />
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-[11px] text-gray-400 flex-1">
                        Introduce yourself or ask a clarifying question — no contact details or price negotiation.
                      </p>
                      <span
                        className={`text-[11px] tabular-nums flex-shrink-0 ${offerData.question.length > 150 ? 'text-red-600 font-medium' : 'text-gray-400'}`}
                        data-testid="bid-question-counter"
                      >
                        {offerData.question.length} / 150
                      </span>
                    </div>
                    {/* Save the current text as a reusable opener (max 3). */}
                    {offerData.question.trim().length >= 5 && !myStarters.includes(offerData.question.trim()) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (myStarters.length >= 3) {
                            toast.error('You can save up to 3 opening messages — delete one first.');
                            return;
                          }
                          persistStarters([...myStarters, offerData.question.trim()]);
                          toast.success('Saved — tap it on any job to reuse.');
                        }}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B5E20] hover:underline"
                        data-testid="bid-save-starter-btn"
                      >
                        <Star className="w-3 h-3" /> Save this message for next time
                      </button>
                    )}
                  </div>

                  {/* Delivery Details (tags) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-gray-800">Delivery details <span className="text-[11px] font-normal text-gray-400">(optional)</span></Label>
                      <span className="text-[11px] text-gray-400 tabular-nums">{offerData.tags.length} / 5</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="offer-tags">
                      {['Available today', 'Within 1 hour', 'Van', 'Car', 'Other vehicle', 'Heavy lifting', 'Furniture experience', 'Insured', 'Experienced driver'].map(tag => {
                        const selected = offerData.tags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              setOfferData(prev => {
                                if (selected) return { ...prev, tags: prev.tags.filter(t => t !== tag) };
                                if (prev.tags.length >= 5) return prev;
                                return { ...prev, tags: [...prev.tags, tag] };
                              });
                            }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${selected ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#2E7D32] hover:text-[#1B5E20]'}`}
                            data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                          >{tag}</button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md h-12 text-base font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.99]"
                    disabled={submitting || askingQuestion || isBiddingClosedFor(selectedJob?.active_bid_count, isPremium)}
                    data-testid="confirm-offer-btn"
                  >
                    {isBiddingClosedFor(selectedJob?.active_bid_count, isPremium) ? 'Bidding closed' : (submitting ? 'Submitting...' : 'Submit Offer')}
                  </Button>

                  {/* Ask first, bid later — sends the question on its own. */}
                  <div className="pt-1" data-testid="ask-before-bidding-block">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAskQuestion}
                      disabled={askingQuestion || submitting}
                      className="w-full h-11 rounded-md border-2 border-[#2E7D32]/30 text-[#1B5E20] hover:bg-emerald-50 hover:border-[#2E7D32]/50 font-semibold"
                      data-testid="ask-question-only-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {askingQuestion ? 'Sending…' : 'Send message without bidding'}
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1.5 text-center">
                      Say hello or ask first, bid later — you can still bid after they reply. Counts towards your 6 messages on this job.
                    </p>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* ============ MY WORK VIEW: Inner toggle between My Jobs / My Offers ============ */}
          {activeView === 'my-work' && (
            <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="my-work-title">My Work</h1>
                <p className="text-sm text-gray-500 mt-1">Track your active jobs and pending bids</p>
              </div>
              <div className="inline-flex p-1 bg-gray-100 rounded-full self-start sm:self-end" role="tablist" data-testid="my-work-toggle">
                <button
                  type="button"
                  onClick={() => setWorkTab('jobs')}
                  className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${workTab === 'jobs' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  data-testid="my-work-tab-jobs"
                  aria-selected={workTab === 'jobs'}
                >
                  My Jobs <span className="ml-1 text-xs text-gray-400">({myJobs.length})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWorkTab('offers')}
                  className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${workTab === 'offers' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  data-testid="my-work-tab-offers"
                  aria-selected={workTab === 'offers'}
                >
                  My Offers <span className="ml-1 text-xs text-gray-400">({myOffers.length})</span>
                </button>
              </div>
            </div>
          )}

          {/* My Jobs — with Active/Recent/History/Archive tabs */}
          {activeView === 'my-work' && workTab === 'jobs' && (
          <div data-testid="my-jobs-content">
            <DeliveryTabs activeCount={myJobs.filter(j => ['assigned','on_the_way'].includes(j.status)).length}>
              {/* Active jobs content */}
              {(() => {
                const activeJobs = myJobs.filter(j => ['assigned','on_the_way'].includes(j.status));
                const JOBS_PP = 5;
                const totalMyJobPages = Math.ceil(activeJobs.length / JOBS_PP);
                const pagedMyJobs = activeJobs.slice((myJobsPage - 1) * JOBS_PP, myJobsPage * JOBS_PP);
                return activeJobs.length === 0 ? (
                <div className="text-center py-10">
                  <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No active jobs — submit offers to get started</p>
                </div>
              ) : (
              <>
              <div className="space-y-4">
                {pagedMyJobs.map((job) => (
                  <Card key={job.id} className="border border-gray-200 shadow-none" data-testid={`my-job-${job.id}`}>
                    <CardContent className="pt-6 space-y-4">
                      {/* Bid won — shown only once the customer's booking is paid
                          and the job hasn't started yet. */}
                      {['authorized', 'paid', 'fees_paid'].includes(job.payment_status) && job.status === 'assigned' && (
                        <div
                          className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5"
                          data-testid={`bid-won-banner-${job.id}`}
                        >
                          <PartyPopper className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-emerald-900">Your bid won — job confirmed</p>
                            <p className="text-xs text-emerald-800 leading-snug mt-0.5">
                              Congratulations! Contact the customer now to arrange the pickup, the drop-off and any other details.
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Payment method badge — always visible so drivers see it
                          the moment a job is assigned, before payment_status flips.
                          Supplementary to the amber 'Direct payment job' notice below. */}
                      {job.payment_method === 'direct' ? (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full border border-amber-200"
                          data-testid={`payment-method-badge-${job.id}`}
                          title="Customer pays you directly using one of your accepted payment methods"
                        >
                          <Banknote className="w-3.5 h-3.5" strokeWidth={2} />
                          Pay Driver Directly
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full border border-blue-200"
                          data-testid={`payment-method-badge-${job.id}`}
                          title="Older booking: the customer's full payment was authorised online and is paid out after verification"
                        >
                          <CreditCard className="w-3.5 h-3.5" strokeWidth={2} />
                          Online Payment (earlier booking)
                        </span>
                      )}
                      {/* Full payment breakdown — spells out fees, cash to collect and payout */}
                      <DriverPaymentBreakdown job={job} />
                      {job.status !== 'completed' && job.status !== 'cancelled' && (
                        <CancelBookingButton mode="driver" delivery={job} onDone={() => fetchData()} />
                      )}

                      {/* Customer ContactCard — visible only AFTER payment committed */}
                      {['authorized', 'paid', 'fees_paid'].includes(job.payment_status) && (
                        <ContactCard
                          name={job.customer_name}
                          phone={job.customer_phone}
                          role="Customer"
                          subtitle={job.payment_method === 'direct' && job.payment_status === 'fees_paid'
                            ? `Collect in cash: €${((job.direct_balance_due ?? job.assigned_price) || 0).toFixed(2)} (Cash / Revolut / Bank)`
                            : `Your payout: €${Number(job.driver_payout ?? (job.assigned_price - driverAccessFeeFor(job.assigned_price))).toFixed(2)}`}
                          dataTestId={`customer-contact-${job.id}`}
                        />
                      )}
                      {job.payment_method === 'direct' && job.payment_status === 'fees_paid' && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2" data-testid={`direct-pay-notice-${job.id}`}>
                          <Banknote className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-semibold text-amber-900">Direct payment job</p>
                            <p className="text-xs text-amber-800 mt-0.5">
                              The customer paid JustDeliver&apos;s fees online. Collect <strong>€{((job.direct_balance_due ?? job.assigned_price) || 0).toFixed(2)}</strong> from them directly via Cash, Revolut or Bank Transfer.
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 grid gap-4 md:grid-cols-4">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Pickup</p>
                            <p className="font-medium text-sm">{job.pickup_location}</p>
                            <span
                              className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 text-sm font-bold tracking-wide"
                              data-testid={`accepted-pickup-eircode-${job.id}`}
                            >
                              <span aria-hidden="true">📍</span>
                              {job.pickup_eircode
                                ? <>Pickup: <span className="font-mono">{job.pickup_eircode}</span></>
                                : <span className="font-normal italic">{isAreaOnly(job) ? 'Eircode after payment' : 'Eircode not provided'}</span>}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Drop-off</p>
                            <p className="font-medium text-sm">{job.dropoff_location}</p>
                            <span
                              className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 text-sm font-bold tracking-wide"
                              data-testid={`accepted-dropoff-eircode-${job.id}`}
                            >
                              <span aria-hidden="true">📍</span>
                              {job.dropoff_eircode
                                ? <>Drop-off: <span className="font-mono">{job.dropoff_eircode}</span></>
                                : <span className="font-normal italic">{isAreaOnly(job) ? 'Eircode after payment' : 'Eircode not provided'}</span>}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Sender (Customer)</p>
                            <p className="font-medium text-sm">{job.customer_name}</p>
                            <p className="text-xs text-gray-500">{job.customer_phone}</p>
                          </div>
                          {(job.receiver_name || job.receiver_phone) && (
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Receiver</p>
                            {job.receiver_name && <p className="font-medium text-sm">{job.receiver_name}</p>}
                            {job.receiver_phone && <p className="text-xs text-gray-500">{job.receiver_phone}</p>}
                          </div>
                          )}
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Your Payout</p>
                            <p className="font-bold text-lg" data-testid={`my-job-payout-${job.id}`}>
                              €{Number(job.driver_payout ?? (job.assigned_price - driverAccessFeeFor(job.assigned_price))).toFixed(2)}
                            </p>
                            <p className="text-[11px] text-gray-500">after JustDeliver fees</p>
                          </div>
                        </div>
                        {/* Item details chips — helps drivers prepare the right vehicle/equipment */}
                        {(job.item_description || job.weight_kg || job.dimensions_length || job.dimensions_width || job.dimensions_height || job.item_type) && (
                          <div className="border-t border-gray-100 pt-3" data-testid={`my-job-item-details-${job.id}`}>
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Item</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-600">
                              {job.item_type && <span className="capitalize font-medium">{job.item_type}</span>}
                              {job.weight_kg && (
                                <span className="flex items-center gap-1"><Weight className="w-3 h-3" /> {job.weight_kg} kg</span>
                              )}
                              {(job.dimensions_length || job.dimensions_width || job.dimensions_height) && (
                                <span className="flex items-center gap-1" data-testid={`my-job-dimensions-${job.id}`}>
                                  <Ruler className="w-3 h-3" />
                                  {[job.dimensions_length, job.dimensions_width, job.dimensions_height].map(v => v ? `${v}` : '?').join(' × ')} cm
                                </span>
                              )}
                            </div>
                            {job.item_description && (
                              <p className="text-xs text-gray-500 mt-1.5 italic">"{job.item_description}"</p>
                            )}
                          </div>
                        )}


                        
                        <div className="flex items-center gap-3 flex-wrap">
                          <StatusBadge status={job.status} />
                          
                          {/* On The Way / Update ETA button for assigned or on_the_way */}
                          {(job.status === 'assigned' || job.status === 'on_the_way') && (
                            <Button
                              variant={job.status === 'on_the_way' ? 'outline' : 'default'}
                              onClick={() => {
                                setEtaJobId(job.id);
                                setEstimatedTime(job.estimated_arrival || '');
                                setEtaDialogOpen(true);
                              }}
                              className={job.status === 'on_the_way' 
                                ? 'border-violet-400 text-violet-700 hover:bg-violet-50 rounded-sm gap-2'
                                : 'bg-violet-600 text-white hover:bg-violet-700 rounded-sm gap-2'
                              }
                              data-testid={`on-the-way-btn-${job.id}`}
                            >
                              <Navigation className="w-4 h-4" strokeWidth={1.5} />
                              {job.status === 'on_the_way' ? 'Update ETA' : 'On The Way'}
                            </Button>
                          )}

                          {/* Show current ETA */}
                          {job.status === 'on_the_way' && job.estimated_arrival && (
                            <span className="text-sm text-violet-700 bg-violet-50 px-3 py-1 border border-violet-200" data-testid={`eta-display-${job.id}`}>
                              ETA: {job.estimated_arrival}
                            </span>
                          )}

                          {/* Per-drop verification — multi-drop deliveries only */}
                          {(job.status === 'assigned' || job.status === 'on_the_way') && Array.isArray(job.drops) && job.drops.length > 0 && (
                            <div className="w-full">
                              <VerifyDropsPanel delivery={job} onChange={fetchData} />
                            </div>
                          )}

                          {/* Confirm Delivery - requires verification */}
                          {(job.status === 'assigned' || job.status === 'on_the_way') && (
                            <Button 
                              onClick={() => handleMarkComplete(job.id)}
                              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm gap-2"
                              data-testid={`complete-job-btn-${job.id}`}
                            >
                              <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                              Confirm Delivery
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {totalMyJobPages > 1 && (
                <div className="flex items-center justify-center gap-1 mt-4">
                  <button onClick={() => setMyJobsPage(p => Math.max(1, p - 1))} disabled={myJobsPage === 1} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Prev</button>
                  {Array.from({ length: totalMyJobPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setMyJobsPage(p)} className={`w-8 h-8 text-sm font-medium rounded-md ${p === myJobsPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>{p}</button>
                  ))}
                  <button onClick={() => setMyJobsPage(p => Math.min(totalMyJobPages, p + 1))} disabled={myJobsPage === totalMyJobPages} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Next</button>
                </div>
              )}
              </>
              );
              })()}
            </DeliveryTabs>
          </div>
          )}

          {/* My Offers */}
          {activeView === 'my-work' && workTab === 'offers' && (
          <div data-testid="my-offers-content">
            {myOffers.length === 0 ? (
              <Card className="border border-gray-200 shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Euro className="w-16 h-16 text-gray-300 mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No offers yet</h3>
                  <p className="text-gray-600">Submit offers on available jobs</p>
                </CardContent>
              </Card>
            ) : (() => {
              const OFFERS_PP = 5;
              const totalOfferPages = Math.ceil(myOffers.length / OFFERS_PP);
              const pagedOffers = myOffers.slice((myOffersPage - 1) * OFFERS_PP, myOffersPage * OFFERS_PP);
              return (
              <>
              <div className="space-y-4">
                {pagedOffers.map((offer) => (
                  <Card key={offer.id} className="border border-gray-200 shadow-none" data-testid={`my-offer-${offer.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Job ID</p>
                          <p className="font-mono text-sm font-semibold text-[#1B5E20]" data-testid={`offer-delivery-ref-${offer.id}`}>
                            {offer.delivery_ref || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Your Price</p>
                          <p className="font-bold text-xl">€{offer.price.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Submitted</p>
                          <p className="text-sm text-gray-600">{formatDate(offer.created_at)}</p>
                        </div>
                        {offer.message && (
                          <div className="flex-1">
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Message</p>
                            <p className="text-sm text-gray-700">{offer.message}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">Status</p>
                          {(() => {
                            // 8-state pill driven by backend `display_status_key`
                            // so drivers see a clear reason at a glance. Legacy
                            // rows without `display_status_key` fall back to the
                            // old 3-state visual (accepted / rejected / pending).
                            const k = offer.display_status_key || offer.status || 'pending';
                            const label = offer.display_status_label || (
                              offer.status === 'accepted' ? 'Accepted'
                              : offer.status === 'rejected' ? 'Rejected'
                              : 'Pending'
                            );
                            const palette = {
                              accepted: 'bg-green-100 text-green-700 border border-green-200',
                              completed: 'bg-green-100 text-green-700 border border-green-200',
                              you_won_awaiting_payment: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
                              rejected: 'bg-red-50 text-red-600 border border-red-200',
                              another_driver_selected: 'bg-red-50 text-red-600 border border-red-200',
                              cancelled_by_customer: 'bg-gray-100 text-gray-700 border border-gray-300',
                              withdrawn: 'bg-gray-100 text-gray-700 border border-gray-300',
                              expired: 'bg-gray-100 text-gray-700 border border-gray-300',
                              pending: 'bg-amber-50 text-amber-700 border border-amber-200',
                            }[k] || 'bg-amber-50 text-amber-700 border border-amber-200';
                            return (
                              <>
                                <span
                                  className={`inline-block px-3 py-1 text-xs font-semibold rounded-sm ${palette}`}
                                  data-testid={`offer-status-${offer.id}`}
                                  data-status-key={k}
                                >
                                  {label}
                                </span>
                                {offer.display_status_reason && (
                                  <p className="mt-1 text-[11px] text-gray-600 leading-snug" data-testid={`offer-status-reason-${offer.id}`}>
                                    {offer.display_status_reason}
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      {/* Edit / Withdraw — only while bid is still pending
                          AND the customer hasn't yet selected another bid
                          (once they have, `you_won_awaiting_payment` /
                          `another_driver_selected` locks the offer). */}
                      {(offer.display_status_key || offer.status) === 'pending' && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100" data-testid={`offer-actions-${offer.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEditOffer(offer)}
                            className="gap-1.5 border-gray-300 rounded-sm hover:bg-gray-50"
                            data-testid={`edit-bid-btn-${offer.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit bid
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleWithdrawOffer(offer)}
                            disabled={withdrawingOfferId === offer.id}
                            className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-sm disabled:opacity-50"
                            data-testid={`withdraw-bid-btn-${offer.id}`}
                          >
                            <X className="w-3.5 h-3.5" /> {withdrawingOfferId === offer.id ? 'Withdrawing…' : 'Withdraw bid'}
                          </Button>
                        </div>
                      )}

                      {/* Ask a Question — collapsed by default */}
                      <div className="mt-4 pt-4 border-t border-gray-100" data-testid={`ask-question-wrapper-${offer.id}`}>
                        <AskAQuestion
                          offerId={offer.id}
                          role="driver"
                          initialBid={offer.message || ''}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {totalOfferPages > 1 && (
                <div className="flex items-center justify-center gap-1 mt-4">
                  <button onClick={() => setMyOffersPage(p => Math.max(1, p - 1))} disabled={myOffersPage === 1} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Prev</button>
                  {Array.from({ length: totalOfferPages }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setMyOffersPage(p)} className={`w-8 h-8 text-sm font-medium rounded-md ${p === myOffersPage ? 'bg-[#2E7D32] text-white' : 'border border-gray-200 hover:bg-gray-50'}`}>{p}</button>
                  ))}
                  <button onClick={() => setMyOffersPage(p => Math.min(totalOfferPages, p + 1))} disabled={myOffersPage === totalOfferPages} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50">Next</button>
                </div>
              )}
              </>
              );
            })()}
          </div>
          )}

        {/* ============ EARNINGS VIEW ============ */}
        {activeView === 'earnings' && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="earnings-view-title">Earnings & Statements</h1>
          <p className="text-sm text-gray-500 mt-1">Track performance, download monthly statements</p>
        </div>
        )}
        {/* Performance & Earnings Stats */}
        {activeView === 'earnings' && earnings && (
          <div className="mb-8 space-y-6" data-testid="driver-stats-section">
            {/* Monthly Highlight Row */}
            <div>
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-[#2E7D32] flex-shrink-0" strokeWidth={1.5} />
                  <h2 className="text-lg font-semibold font-['Inter'] text-gray-900 truncate">{earnings.current_month || 'This Month'}</h2>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <select
                    value={earningsPeriod}
                    onChange={(e) => setEarningsPeriod(e.target.value)}
                    className="flex-1 sm:flex-none h-9 px-3 text-sm border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                    data-testid="earnings-period-filter"
                  >
                    <option value="this_month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="this_year">This Year</option>
                    <option value="all_time">All Time</option>
                  </select>
                  <DownloadStatementDialog triggerClassName="h-9 px-3 text-sm rounded-md whitespace-nowrap" />
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Card className="border-2 border-[#2E7D32]/30 bg-[#2E7D32]/5 shadow-none" data-testid="stat-monthly-earnings">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <p className="text-2xl sm:text-3xl font-bold text-[#2E7D32] tabular-nums leading-tight break-words">€{earnings.period_earned?.toFixed(2) || earnings.monthly_earned?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs sm:text-sm text-[#1B5E20] mt-1 font-medium">Earned ({earnings.current_month})</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-200 bg-blue-50 shadow-none" data-testid="stat-monthly-jobs">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <p className="text-2xl sm:text-3xl font-bold text-blue-700 tabular-nums leading-tight">{earnings.period_jobs ?? earnings.jobs_completed_month ?? 0}</p>
                    <p className="text-xs sm:text-sm text-blue-600 mt-1 font-medium">Jobs ({earnings.current_month})</p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-emerald-200 bg-emerald-50 shadow-none" data-testid="stat-total-earnings">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <p className="text-2xl sm:text-3xl font-bold text-emerald-800 tabular-nums leading-tight break-words">€{earnings.total_earned?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs sm:text-sm text-emerald-700 mt-1 font-medium">Total earnings</p>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-none" data-testid="stat-total-completed">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums leading-tight">{earnings.jobs_completed_total || 0}</p>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1 font-medium">Jobs completed</p>
                  </CardContent>
                </Card>
              </div>

              {/* PR 2: Split earnings — Confirmed Stripe vs Estimated Direct */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-4" data-testid="earnings-split-cards">
                <Card className="border-2 border-blue-200 bg-blue-50/60 shadow-none" data-testid="stat-confirmed-stripe">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-blue-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Earlier Online-Paid Earnings</p>
                        <p className="text-2xl sm:text-3xl font-bold text-blue-900 tabular-nums leading-tight mt-1">€{(earnings.confirmed_period_earned ?? earnings.period_earned ?? 0).toFixed(2)}</p>
                        <p className="text-[11px] text-blue-700 mt-1">
                          Bookings paid out by JustDeliver under the previous model · {earnings.confirmed_jobs_total ?? earnings.total_deliveries ?? 0} delivered all-time (€{(earnings.confirmed_total_earned ?? earnings.total_earned ?? 0).toFixed(2)})
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200 bg-amber-50/60 shadow-none" data-testid="stat-estimated-direct">
                  <CardContent className="px-4 pt-5 pb-4 sm:pt-6 sm:pb-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <Banknote className="w-5 h-5 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Paid Directly By Customers</p>
                        <p className="text-2xl sm:text-3xl font-bold text-amber-900 tabular-nums leading-tight mt-1">€{(earnings.direct_period_earned ?? 0).toFixed(2)}</p>
                        <p className="text-[11px] text-amber-700 mt-1">
                          Collected directly from customers · {earnings.direct_jobs_period ?? 0} this period · {earnings.direct_jobs_total ?? 0} all-time (€{(earnings.direct_total_earned ?? 0).toFixed(2)})
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Performance Metrics Row */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
                <h2 className="text-lg font-semibold font-['Inter'] text-gray-900">Performance</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <Card className="border border-gray-200 shadow-none" data-testid="stat-avg-per-job">
                  <CardContent className="px-4 pt-4 pb-3 sm:pt-5 sm:pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 flex items-center justify-center rounded flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base sm:text-xl font-bold tabular-nums truncate">€{earnings.avg_per_job?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-500 truncate">Avg / Job</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-none" data-testid="stat-completion-rate">
                  <CardContent className="px-4 pt-4 pb-3 sm:pt-5 sm:pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center rounded flex-shrink-0">
                        <Target className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-base sm:text-xl font-bold tabular-nums truncate">{earnings.completion_rate || 0}%</p>
                        <p className="text-xs text-gray-500 truncate">Completion Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {activeView === 'directory' && DIRECTORY_ENABLED && (<>
          <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-['Inter'] text-gray-900">Public Profile</h1>
              <p className="text-sm text-gray-500 mt-1">
                {directoryMode === 'edit'
                  ? 'Edit your profile in-place. One save button submits everything for admin review.'
                  : 'This is how customers see your public profile.'}
              </p>
            </div>
          </div>
        </>)}

        {activeView === 'directory' && DIRECTORY_ENABLED && (
        <CollapsibleDirectoryEnquiries />
        )}

        {activeView === 'directory' && DIRECTORY_ENABLED && (
        <MyPublicProfileView
          editing={directoryMode === 'edit'}
          onEdit={() => setDirectoryMode('edit')}
          onExitEdit={() => setDirectoryMode('view')}
        />
        )}

        {/* ETA Dialog */}
        <Dialog open={etaDialogOpen} onOpenChange={setEtaDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="eta-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter'] flex items-center gap-2">
                <Navigation className="w-5 h-5 text-violet-600" />
                {myJobs.find(j => j.id === etaJobId)?.status === 'on_the_way' ? 'Update ETA' : 'On The Way'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-gray-600">
                Let the customer know when you'll arrive. They'll see your status and estimated time.
              </p>
              <div className="space-y-2">
                <Label htmlFor="eta" className="text-sm font-medium">Expected Arrival Time</Label>
                <Input
                  id="eta"
                  type="time"
                  value={estimatedTime}
                  onChange={(e) => setEstimatedTime(e.target.value)}
                  className="rounded-sm"
                  data-testid="eta-time-input"
                />
              </div>
              <Button
                onClick={handleOnTheWay}
                disabled={!estimatedTime || updatingStatus}
                className="w-full bg-violet-600 text-white hover:bg-violet-700 rounded-sm gap-2"
                data-testid="confirm-on-the-way-btn"
              >
                <Navigation className="w-4 h-4" />
                {updatingStatus ? 'Updating...' : 'Confirm & Notify Customer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delivery Verification Dialog */}
        <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
          <DialogContent className="max-w-md" data-testid="verify-delivery-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter'] flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-[#2E7D32]" />
                Confirm Delivery
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              <Alert className="border-emerald-200 bg-emerald-50">
                <KeyRound className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800 text-sm">
                  Enter the <strong>6-digit code</strong> from the customer to confirm delivery. This protects you, prevents disputes, and builds trust on JustDeliver.
                </AlertDescription>
              </Alert>

              {/* Code Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Verification Code</Label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={verifyCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerifyCode(val);
                    setVerifyError('');
                  }}
                  className="text-center text-2xl font-mono tracking-[0.5em] rounded-sm h-14"
                  data-testid="verify-code-input"
                />
                <p className="text-xs text-gray-400">The customer will give you this code once the item is successfully delivered.</p>
              </div>

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Proof of Delivery Photo</Label>
                <p className="text-xs text-gray-400 -mt-1">Helps prevent disputes and strengthens your job history.</p>
                {verifyPhotoPreview ? (
                  <div className="relative">
                    <img src={verifyPhotoPreview} alt="Proof" className="w-full max-h-48 object-cover border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => { setVerifyPhoto(null); setVerifyPhotoPreview(null); }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 py-8 cursor-pointer hover:border-[#2E7D32] transition-colors" data-testid="verify-photo-upload">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Upload proof of delivery</span>
                    <span className="text-xs text-gray-400 mt-1">Photo of delivered item at drop-off</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleVerifyPhotoChange} />
                  </label>
                )}
              </div>

              {/* Error */}
              {verifyError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">{verifyError}</AlertDescription>
                </Alert>
              )}

              {/* Trust note */}
              <p className="text-xs text-gray-400 text-center italic">Verified deliveries help you build a stronger profile and get more jobs.</p>

              {/* Submit */}
              <Button
                onClick={handleVerifyDelivery}
                disabled={verifying || !verifyCode || verifyCode.length !== 6 || !verifyPhoto}
                className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm gap-2 h-12"
                data-testid="confirm-verify-btn"
              >
                <CheckCircle className="w-5 h-5" />
                {verifying ? 'Verifying...' : 'Confirm Delivery'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Bid Dialog */}
        <Dialog open={editOfferDialogOpen} onOpenChange={(o) => { setEditOfferDialogOpen(o); if (!o) setEditingOffer(null); }}>
          <DialogContent className="max-w-md" data-testid="edit-offer-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Edit your bid</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-xs">
                  Updating your bid will notify the customer. You can edit until the customer accepts an offer.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Your price (€)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  max={MAX_BID_AMOUNT}
                  value={editOfferForm.price}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return setEditOfferForm({ ...editOfferForm, price: '' });
                    const n = parseFloat(raw);
                    const next = Number.isFinite(n) && n > MAX_BID_AMOUNT ? String(MAX_BID_AMOUNT) : raw;
                    setEditOfferForm({ ...editOfferForm, price: next });
                  }}
                  className="rounded-sm h-11 text-base font-semibold"
                  data-testid="edit-offer-price"
                />
                {Number(editOfferForm.price) > MAX_BID_AMOUNT ? (
                  <p className="text-xs text-red-600 font-medium" data-testid="edit-bid-max-error">
                    {MAX_BID_ERROR}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">Maximum bid: €{MAX_BID_AMOUNT.toLocaleString()}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Message (optional · max 150 chars)</Label>
                <Textarea
                  value={editOfferForm.message}
                  onChange={(e) => setEditOfferForm({ ...editOfferForm, message: e.target.value.slice(0, 150) })}
                  rows={3}
                  maxLength={150}
                  placeholder="Ask a question or add context (no contact details, no negotiation)…"
                  className="rounded-sm"
                  data-testid="edit-offer-message"
                />
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[11px] text-gray-500 flex-1">
                    No phone numbers, emails, websites, or price negotiation — contact details are shared automatically after payment.
                  </p>
                  <span className={`text-[11px] tabular-nums flex-shrink-0 ${editOfferForm.message.length > 150 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                    {editOfferForm.message.length} / 150
                  </span>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditOfferDialogOpen(false)} className="flex-1 border-gray-300 rounded-sm" data-testid="edit-offer-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveOfferEdit}
                  disabled={editOfferSubmitting}
                  className="flex-1 bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm gap-2 disabled:opacity-60"
                  data-testid="edit-offer-save-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  {editOfferSubmitting ? 'Saving…' : 'Save bid'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      {/* Photo lightbox — full-size view of customer item photos */}
      {galleryPhotos && (
        <Dialog open onOpenChange={(o) => { if (!o) { setGalleryPhotos(null); setGalleryIdx(0); } }}>
          <DialogContent className="max-w-3xl bg-black/95 border-0 p-0 overflow-hidden" data-testid="photo-lightbox">
            <DialogHeader className="sr-only">
              <DialogTitle>Item photo {galleryIdx + 1} of {galleryPhotos.length}</DialogTitle>
            </DialogHeader>
            <div className="relative">
              <img
                src={resolveAssetUrl(galleryPhotos[galleryIdx])}
                alt={`Item photo ${galleryIdx + 1}`}
                className="block w-full max-h-[80vh] object-contain bg-black"
              />
              {galleryPhotos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setGalleryIdx((i) => (i - 1 + galleryPhotos.length) % galleryPhotos.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black text-xl font-bold flex items-center justify-center shadow-md"
                    aria-label="Previous photo"
                    data-testid="lightbox-prev"
                  >‹</button>
                  <button
                    type="button"
                    onClick={() => setGalleryIdx((i) => (i + 1) % galleryPhotos.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black text-xl font-bold flex items-center justify-center shadow-md"
                    aria-label="Next photo"
                    data-testid="lightbox-next"
                  >›</button>
                  <span className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-semibold">
                    {galleryIdx + 1} / {galleryPhotos.length}
                  </span>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      </main>
      <DashboardFooter />
      <SupportChat />
    </div>
  );
}

/**
 * FilterPill — a compact dropdown pill used by the Available Jobs filter row.
 * Renders an icon + "Label: value" + chevron, with the native <select>
 * stretched invisibly over the whole pill so taps anywhere open it. Pass the
 * <select> via `children`. Used for Drop-off / Item type / Sort.
 */
function FilterPill({ Icon, prefix, value, children, testId }) {
  return (
    <label
      className="relative flex items-center gap-2.5 bg-white border border-gray-200 rounded-2xl px-3.5 py-3 cursor-pointer hover:bg-gray-50 transition-colors min-h-[48px]"
      data-testid={testId}
    >
      <Icon className="w-[18px] h-[18px] text-gray-500 flex-shrink-0" strokeWidth={1.75} />
      <span className="flex-1 min-w-0 text-sm sm:text-base font-semibold text-gray-900 truncate">
        {prefix && <span className="text-gray-500 font-normal mr-1">{prefix}</span>}
        {value}
      </span>
      <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
      {children}
    </label>
  );
}

